export const LOCALES = ['en', 'fr', 'nl', 'de'] as const

export type Locale = (typeof LOCALES)[number]

const EN_MESSAGES = {
  'meta.title': 'Belgium in Maps',
  'meta.description': 'Explore official Belgian statistics on an honest, source-backed map.',
  'language.label': 'Language',
  'language.en': 'English',
  'language.fr': 'Français',
  'language.nl': 'Nederlands',
  'language.de': 'Deutsch',
  'brand.home': 'Belgium in Maps home',
  'brand.country': 'Belgium',
  'brand.suffix': 'in maps',
  'header.aboutData': 'About the data',
  'header.copyLink': 'Copy link',
  'header.linkCopied': 'Link copied',
  'header.copyFallback': 'Copy the URL from your browser',
  'export.download': 'Download PNG',
  'export.resolution': 'PNG resolution',
  'export.twoX': '2× standard',
  'export.threeX': '3× high resolution',
  'export.preparing': 'Preparing the high-resolution PNG…',
  'export.preparingShort': 'Preparing…',
  'export.complete': 'PNG downloaded.',
  'export.failed': 'The PNG could not be created. Try again.',
  'export.filters': 'Filters',
  'export.scope': 'Map extent',
  'export.scopeFull': 'Full Belgium',
  'export.scopeCurrent': 'Current map view',
  'export.scopeSelected': 'Selected area',
  'export.scopeSelectedUnavailable': 'Selected area (select one first)',
  'common.retry': 'Retry',
  'common.close': 'Close',
  'common.noData': 'No data',
  'common.suppressed': 'Suppressed or confidential',
  'common.unavailable': 'Unavailable',
  'status.belowThreshold': 'Below publication threshold',
  'status.hidden': 'Hidden by the publisher',
  'status.notApplicable': 'Not applicable',
  'status.missing': 'Marked missing by the publisher',
  'status.empty': 'Empty source cell',
  'common.source': 'Source',
  'common.officialDownload': 'Official download ↗',
  'common.notAvailable': '—',
  'common.percentagePointsShort': 'pp',
  'filters.title': 'Filters',
  'filters.all': 'All',
  'filters.clearAll': 'Clear filters',
  'filters.selected': '{count} selected',
  'color.title': 'Colour scale',
  'color.palette': 'Palette',
  'color.sequential': 'Sequential',
  'color.diverging': 'Diverging',
  'color.categorical': 'Categorical',
  'color.fixedScale': 'Fixed comparison scale',
  'color.currentScale': 'Current selection scale',
  'color.low': 'Low',
  'color.high': 'High',
  'color.midpoint': 'Reference midpoint',
  'color.missing': 'Missing',
  'color.suppressed': 'Suppressed or confidential',
  'palette.default': 'Scientific default',
  'palette.viridis': 'Viridis — CVD-safe',
  'palette.cividis': 'Cividis — CVD-safe',
  'palette.magma': 'Magma',
  'palette.turbo': 'Turbo — advanced, non-analytical',
  'palette.jet': 'Jet — advanced, non-analytical',
  'palette.blueRed': 'Blue–red',
  'palette.purpleGreen': 'Purple–green',
  'palette.brownTeal': 'Brown–teal',
  'palette.advancedGroup': 'Advanced / exploratory',
  'palette.custom': 'Custom palette — advanced',
  'palette.customColors': 'Ordered custom colours',
  'palette.colorStop': 'Colour {index}',
  'palette.addColor': 'Add a colour',
  'palette.removeColor': 'Remove the last colour',
  'palette.customHelp': 'Advanced exploratory choice. Your ordered colours run from low to high; perceptual uniformity and colour-vision accessibility are not guaranteed.',
  'palette.sequentialHelp': 'Perceptually ordered colours preserve an honest low-to-high progression.',
  'palette.divergingHelp': 'Diverging colours show distance on both sides of a meaningful midpoint.',
  'palette.cvdHelp': 'Designed to remain readable with common colour-vision deficiencies.',
  'palette.nonUniformWarning': 'Advanced, non-analytical choice. This palette is perceptually non-uniform, so equal data steps may look unequal.',
  'classes.title': 'Classes',
  'classes.mode': 'Class method',
  'classes.catalog': 'Scientific default',
  'classes.advancedGroup': 'Advanced / exploratory',
  'classes.adaptive': 'Adaptive — advanced current slice',
  'classes.custom': 'Choose a count — advanced',
  'classes.count': 'Requested classes',
  'classes.catalogFixedHelp': 'Uses the {actual} catalogue classes, keeping colours comparable across approved periods.',
  'classes.catalogFallbackHelp': 'No fixed reference scale applies to this filter slice. Its {actual} classes are recalculated from the current data; colours are not comparable with another slice.',
  'classes.adaptiveHelp': 'Advanced exploratory mode targets {target} classes from the usable, distinct values; ties or scientific constraints produce {actual}. Signed measures remain symmetric around their reference with an odd class count. Colours may change between selections.',
  'classes.customHelp': 'Advanced exploratory mode requests {requested} classes; ties or scientific constraints produce {actual}. Signed measures enforce a symmetric odd class count, so the actual count may differ. Colours are not comparable between selections.',
  'classes.adaptiveNote': 'Advanced current-slice classification (target {target}; actual {actual})',
  'classes.customNote': 'Advanced current-slice classification (requested {requested}; actual {actual})',
  'boundaries.title': 'Area boundaries',
  'boundaries.help': 'Show boundary lines on the map and in PNG downloads.',
  'charts.title': 'Chart',
  'charts.mapView': 'Map',
  'charts.tableView': 'Table',
  'charts.trend': 'Trend over time',
  'charts.composition': 'Composition',
  'charts.distribution': 'Distribution',
  'charts.comparison': 'Area comparison',
  'charts.noData': 'No chart data are available for this selection.',
  'charts.loading': 'Loading comparable periods…',
  'charts.trendAria': '{metric} trend for {area}',
  'charts.comparableOnly': 'The vertical axis is fitted to the displayed values. Only methodologically comparable periods are connected; a municipality series stops at an audited merger or territorial change.',
  'charts.period': 'Period',
  'charts.value': 'Value',
  'charts.pointCount': '{count} comparable observations.',
  'charts.tablePagination': 'Trend table pages',
  'charts.tableRange': '{start}–{end} of {count} observations',
  'metric.measureTitle': '{metric}: {measure}',
  'period.referenceDate': 'Reference date: {date}',
  'period.observationDate': 'Observation date: {date}',
  'period.releaseDate': 'Release date: {date}',
  'period.serviceDate': 'Scheduled service date: {date}',
  'period.coveragePeriod': 'Coverage period: {start} – {end}',
  'period.plannedSchedule': 'Published timetable; planned service, not observed operations.',
  'boot.loading': 'Loading Belgium’s official data…',
  'boot.unavailable': 'The atlas catalogue is unavailable.',
  'error.catalogContract': 'The published catalogue did not pass its declared v2 data contract.',
  'error.catalogLoad': 'The official data catalogue could not be loaded.',
  'error.catalogRequest': 'Catalogue request failed ({status}).',
  'error.boundaryRequest': 'Boundary request failed ({status}).',
  'error.valuePackRequest': 'Value-pack request failed ({status}).',
  'error.boundaryCount': 'Boundary feature count mismatch: expected {expected}, received {received}.',
  'error.boundaryLoad': 'The selected boundary pack could not be loaded.',
  'error.valueContract': 'The selected value pack failed its identity, boundary, level, or QA contract.',
  'error.valuesLoad': 'The selected values could not be loaded.',
  'error.mapWebgl': 'WebGL could not start. The same data remain available in the table view.',
  'error.mapRenderer': 'The WebGL map renderer reported an error.',
  'map.title': '{metric} by {geography}',
  'map.ariaLabel': 'Interactive statistical map of Belgium',
  'map.canvasLabel': 'Map',
  'map.zoomIn': 'Zoom in',
  'map.zoomOut': 'Zoom out',
  'map.areasDisplayed': '{count} {level} areas displayed.',
  'map.loading': 'Loading map.',
  'map.selectedDetails': 'Selected area details',
  'map.closeSelected': 'Close selected area',
  'map.provinceEquivalent': 'Brussels-Capital is shown as a province-equivalent.',
  'map.viewDefinition': 'View definition & source →',
  'map.delta': '{delta} {unit} vs {period}',
  'map.updating': 'Updating {count} areas…',
  'map.unavailable': 'Map unavailable',
  'map.browseTable': 'Browse data as a table',
  'map.methodology': 'Methodology',
  'map.selectedAnnouncement': '{name} selected. {value} {unit}, {period}.',
  'sidebar.closeMapControls': 'Close map controls',
  'sidebar.mapControls': 'Map controls',
  'sidebar.closeControls': 'Close controls',
  'sidebar.officialData': 'Official Belgian data',
  'sidebar.boundaries': 'Boundaries {vintage}',
  'sidebar.heading': '{metric} by {geography}',
  'sidebar.measure': 'Measure',
  'sidebar.geography': 'Geography',
  'sidebar.levelUnavailable': '{level} — unavailable',
  'sidebar.provinceNote': 'Includes Brussels-Capital as a province-equivalent so Belgium remains complete.',
  'sidebar.period': 'Period',
  'sidebar.previousPeriod': 'Previous period',
  'sidebar.nextPeriod': 'Next period',
  'sidebar.dataContext': 'Data: {label}',
  'sidebar.mapContext': 'Map: {label}',
  'sidebar.onlyPeriod': 'Only {period} is available at this geography.',
  'sidebar.dataDetails': 'Data details',
  'sidebar.browseTable': 'Browse as table',
  'level.region': 'Region',
  'level.province': 'Province + Brussels',
  'level.district': 'Administrative district',
  'level.municipality': 'Municipality',
  'level.sector': 'Statistical sector',
  'level.regionPlural': 'Regions',
  'level.provincePlural': 'Provinces + Brussels',
  'level.districtPlural': 'Administrative districts',
  'level.municipalityPlural': 'Municipalities',
  'level.sectorPlural': 'Statistical sectors',
  'indicator.label': 'Indicator',
  'indicator.otherTopic': 'Other indicators',
  'indicator.noneAvailable': 'No indicators available',
  'indicator.searchLabel': 'Search indicators',
  'indicator.searchPlaceholder': 'Search indicators or topics',
  'indicator.countOne': '{count} indicator',
  'indicator.countMany': '{count} indicators',
  'indicator.showMore': 'Show {count} more',
  'indicator.listLabel': 'Indicators',
  'indicator.current': 'Current',
  'indicator.noMatch': 'No indicators match “{query}”.',
  'indicator.browseCategory': 'Browse by category',
  'indicator.categoryFilter': 'Indicator category',
  'indicator.clearCategory': 'Clear category filter',
  'indicator.section.recent': 'Recently viewed',
  'indicator.badgesLabel': 'Indicator scope and source type',
  'indicator.scopeMixed': 'Mixed scope',
  'indicator.sourceType.officialRegister': 'Official register',
  'indicator.sourceType.administrativeHealthcare': 'Administrative healthcare',
  'indicator.sourceType.surveyEstimate': 'Survey estimate',
  'indicator.sourceType.scheduledService': 'Scheduled service',
  'indicator.sourceType.remoteSensing': 'Remote-sensing derived',
  'indicator.sourceType.modelled': 'Modelled',
  'indicator.sourceType.europeanHarmonized': 'European harmonized',
  'search.label': 'Search a place or official code',
  'search.placeholder': 'Search place or code',
  'search.clear': 'Clear search',
  'search.noMatch': 'No matching area',
  'legend.label': 'Map legend',
  'legend.classes': '{count} classes',
  'legend.class': '{count} class',
  'legend.lessThan': '< {end}',
  'legend.atLeast': '≥ {start}',
  'legend.range': '{start} – < {end}',
  'legend.equal': '= {value}',
  'legend.closedRange': '{start} – {end}',
  'legend.fixedBreaks': 'Fixed {method} breaks{period}',
  'legend.periodSuffix': ' · {period}',
  'legend.areaCoverage': '{mapped} of {total} areas',
  'legend.residentCoverage': ' · {percent}% of residents mapped',
  'legend.statusCount': '{label} · {count}',
  'legend.scope': 'Scope: {scope}',
  'legend.symbols': 'Proportional symbols',
  'legend.symbolNote': 'Circle area is proportional to the value. Area fills provide geographic context.',
  'legend.symbolDistance': 'Distance from {center}: {distance}',
  'legend.symbolDistanceNote': 'Circle area is proportional to the absolute distance from the reference value {center}. Area fills provide geographic context.',
  'details.eyebrow': 'Methodology',
  'details.title': 'About this map',
  'details.close': 'Close data details',
  'details.measure': 'Measure',
  'details.unit': 'Unit',
  'details.period': 'Period',
  'details.geography': 'Geography',
  'details.boundaryPack': 'Boundary pack',
  'details.geographyBasis': 'Geography basis',
  'details.basisNative': 'Native historical boundaries',
  'details.basisHarmonized': 'Harmonized geography',
  'details.basisMixed': 'Source-defined retrospective geography',
  'details.coverageTitle': 'Coverage & classification',
  'details.mappedAreas': 'Mapped areas',
  'details.coverageScope': 'Data scope',
  'details.comparisonScope': 'Comparison scope',
  'details.rankingScope': 'Ranking scope',
  'details.rankingRegionalOnly': 'Within the declared region only',
  'details.scopeBelgium': 'Belgium',
  'details.scopeFlanders': 'Flanders',
  'details.scopeWallonia': 'Wallonia',
  'details.scopeBrussels': 'Brussels-Capital',
  'details.scopeParticipating': 'Participating municipalities',
  'details.scopeSourceDefined': 'Source-defined coverage',
  'details.participatingAreas': 'Areas in scope',
  'details.outOfScope': 'Outside scope',
  'details.inScopeUnavailable': 'Unavailable within scope',
  'details.residentCoverage': 'Resident coverage',
  'details.classMethod': 'Class method',
  'details.timeComparison': 'Time comparison',
  'details.comparisonEnabled': 'Enabled for this comparable series',
  'details.comparisonByLineage': 'Enabled while the selected municipality territory is unchanged; mergers create a break',
  'details.comparisonDisabled': 'Not shown across incompatible slices',
  'details.buildQa': 'Build QA',
  'details.qaPassed': '{passed} / {total} checks passed',
  'details.currentQuintiles': 'Current-slice quintiles',
  'details.fixedClassification': 'Class breaks are fixed within the declared comparison set for this indicator, measure and geography.',
  'details.fallbackClassification': 'No pooled classification was published for this slice, so the map uses current-slice quintiles.',
  'details.userClassification': 'Your class override recalculates breaks from the current slice. It is useful for exploration, but map colours are not directly comparable between selections; source values and approved trend comparisons are unchanged.',
  'details.zeroMissing': 'Zero is retained as a valid value; missing observations are shown separately.',
  'details.geographicIntegrity': 'Geographic integrity',
  'details.integrityCodes': 'NIS/REFNIS and statistical-sector codes are treated as opaque text keys. The build joins Statbel’s explicit sector, municipality, province and region fields; it never infers a parent geography by slicing a code. Brussels-Capital is clearly marked as a province-equivalent in the province view.',
  'details.integrityBoundaries': 'Every observation slice is bound to an immutable, source-pinned boundary pack. The map changes with the selected period whenever Statbel’s statistical sectors or administrative boundaries changed. Cross-period deltas are shown only when the catalogue explicitly marks the comparison as valid. Non-locatable sector records are disclosed in coverage and retained in higher-grain totals only when an explicit parent key is available.',
  'details.sources': 'Sources',
  'details.licencePrefix': 'Published source licences:',
  'details.licenceSuffix': 'Every transformation and geometry operation is documented in the source metadata.',
  'details.reuseTermsNotExplicit': 'Public official distribution; no explicit licence badge was supplied. Reused with attribution under the project policy.',
  'details.sourceAccessed': 'Source accessed:',
  'note.aimCodes': 'AIM/IMA {domain} codes were verified and normalized to bare five-digit REFNIS codes; labels are used only for table reconciliation.',
  'note.biptExactCodes': 'Exact BIPT NIS codes are joined to the matching annual Statbel boundary pack; no cross-vintage comparison is inferred.',
  'note.fixedNutsGdp': 'Fixed NUTS 2024 geography. Verviers total GDP is the exact additive sum of BE335 and BE336; its non-additive GDP-per-inhabitant value is deliberately unavailable.',
  'note.exactMunicipalityCodes': 'Source municipality codes are joined exactly to the reviewed official boundary domain; the parent hierarchy comes from boundary feature properties.',
  'note.ghslFixedLau': 'Remote-sensing-derived and modelled built-up surface on fixed GISCO 2020 LAU boundaries.',
  'note.brusselsProjection': 'Brussels-only official projection on the fixed current 19-municipality domain; other municipalities are out of scope.',
  'note.sourceNativeDistrict': 'Source-native district observations; no higher-grain value is assigned to a lower geography.',
  'note.coordinateInventory': 'Coordinate-derived inventory; zero is valid data and excluded points remain unassigned.',
  'note.inventorySnapshot': 'Inventory snapshot at the source data-processing date; it is not a historical flow measure.',
  'note.gtfsCoordinates': 'GTFS stop coordinates are joined to hash-verified official municipality polygons; no source identifier is interpreted as a municipality hierarchy.',
  'note.regionalCurrentDomain': 'Current municipality domain for {scope} only; all other municipalities are explicitly out of scope.',
  'note.sourceNativeMunicipality': 'Source-native municipality observations; no higher-grain value is assigned to a lower geography.',
  'note.clcIntersection': 'Official CLC2018 polygons intersected with hash-verified official Statbel 2018 municipality polygons in EPSG:3035.',
  'note.sparseCubeCoverage': 'Sparse-cube coverage is recalculated after the selected filters are applied; zero remains valid data.',
  'note.regionalCoverage': 'Municipalities outside the declared regional scope are explicitly unavailable, not statistically missing; publisher-side unavailable and suppressed statuses remain separate within scope.',
  'note.comparisonDeclaredGroup': 'Direct comparison is limited to this declared comparison group and immutable boundary pack; no cross-boundary inference is made.',
  'note.comparisonSourceDomain': 'Direct comparisons are limited to the source-declared code-domain group.',
  'note.comparisonSingleSnapshot': 'Single 1 January {year} snapshot on the native {year} boundary pack.',
  'note.comparisonMixedBases': 'Direct deltas are disabled because the comparison set mixes source-classification and native-boundary bases.',
  'note.comparisonBoundaryPacks': 'Direct deltas are disabled because the comparison set crosses native boundary packs.',
  'note.comparisonAirFixed': 'All years use IRCEL-CELINE’s fixed post-{year} municipality classification.',
  'note.comparisonRegional': 'Comparison and ranking are limited to the {count} current municipalities in {scope}; municipalities elsewhere are out of scope, not missing observations. No Belgium-wide rank is valid.',
  'note.comparisonEurostatGdp': 'Nominal current-price series on fixed NUTS 2024 geography. Eurostat flags 2009 as a break and 2024 as provisional. Per-inhabitant ranking covers 42 districts; Verviers has only an additive total-GDP value because its two NUTS-3 ratios are not rolled up.',
  'table.eyebrow': 'Accessible data view · {period}',
  'table.close': 'Close data table',
  'table.filterLabel': 'Filter areas',
  'table.filterPlaceholder': 'Filter by area or code',
  'table.areaCount': '{count} areas',
  'table.caption': '{title}, {period}.',
  'table.sortHint': 'Select a column heading to sort this table.',
  'table.sortedAscending': 'Sorted by {column}, ascending.',
  'table.sortedDescending': 'Sorted by {column}, descending.',
  'table.area': 'Area',
  'table.officialCode': 'Official code',
  'table.previous': '← Previous',
  'table.next': 'Next →',
  'table.page': 'Page {page} of {count}',
} as const

export type MessageKey = keyof typeof EN_MESSAGES
export type MessageParams = Record<string, string | number>
export type Translator = (key: MessageKey, params?: MessageParams) => string

const FR_MESSAGES: Record<MessageKey, string> = {
  'meta.title': 'La Belgique en cartes',
  'meta.description': 'Explorez les statistiques officielles belges sur une carte transparente et documentée.',
  'language.label': 'Langue',
  'language.en': 'English',
  'language.fr': 'Français',
  'language.nl': 'Nederlands',
  'language.de': 'Deutsch',
  'brand.home': 'Accueil de La Belgique en cartes',
  'brand.country': 'Belgique',
  'brand.suffix': 'en cartes',
  'header.aboutData': 'À propos des données',
  'header.copyLink': 'Copier le lien',
  'header.linkCopied': 'Lien copié',
  'header.copyFallback': 'Copiez l’URL depuis votre navigateur',
  'export.download': 'Télécharger le PNG',
  'export.resolution': 'Résolution du PNG',
  'export.twoX': '2× standard',
  'export.threeX': '3× haute résolution',
  'export.preparing': 'Préparation du PNG haute résolution…',
  'export.preparingShort': 'Préparation…',
  'export.complete': 'PNG téléchargé.',
  'export.failed': 'Impossible de créer le PNG. Réessayez.',
  'export.filters': 'Filtres',
  'export.scope': 'Étendue de la carte',
  'export.scopeFull': 'Toute la Belgique',
  'export.scopeCurrent': 'Vue actuelle de la carte',
  'export.scopeSelected': 'Zone sélectionnée',
  'export.scopeSelectedUnavailable': 'Zone sélectionnée (sélectionnez-en une)',
  'common.retry': 'Réessayer',
  'common.close': 'Fermer',
  'common.noData': 'Pas de données',
  'common.suppressed': 'Masqué ou confidentiel',
  'common.unavailable': 'Indisponible',
  'status.belowThreshold': 'Sous le seuil de publication',
  'status.hidden': 'Masqué par l’éditeur',
  'status.notApplicable': 'Sans objet',
  'status.missing': 'Déclaré manquant par l’éditeur',
  'status.empty': 'Cellule source vide',
  'common.source': 'Source',
  'common.officialDownload': 'Téléchargement officiel ↗',
  'common.notAvailable': '—',
  'common.percentagePointsShort': 'pp',
  'filters.title': 'Filtres',
  'filters.all': 'Tous',
  'filters.clearAll': 'Effacer les filtres',
  'filters.selected': '{count} sélectionnés',
  'color.title': 'Échelle de couleurs',
  'color.palette': 'Palette',
  'color.sequential': 'Séquentielle',
  'color.diverging': 'Divergente',
  'color.categorical': 'Catégorielle',
  'color.fixedScale': 'Échelle de comparaison fixe',
  'color.currentScale': 'Échelle de la sélection actuelle',
  'color.low': 'Faible',
  'color.high': 'Élevé',
  'color.midpoint': 'Point de référence',
  'color.missing': 'Manquant',
  'color.suppressed': 'Masqué ou confidentiel',
  'palette.default': 'Palette scientifique par défaut',
  'palette.viridis': 'Viridis — lisible en cas de daltonisme',
  'palette.cividis': 'Cividis — lisible en cas de daltonisme',
  'palette.magma': 'Magma',
  'palette.turbo': 'Turbo — avancé, non analytique',
  'palette.jet': 'Jet — avancé, non analytique',
  'palette.blueRed': 'Bleu–rouge',
  'palette.purpleGreen': 'Violet–vert',
  'palette.brownTeal': 'Brun–bleu-vert',
  'palette.advancedGroup': 'Avancé / exploratoire',
  'palette.custom': 'Palette personnalisée — avancée',
  'palette.customColors': 'Couleurs personnalisées ordonnées',
  'palette.colorStop': 'Couleur {index}',
  'palette.addColor': 'Ajouter une couleur',
  'palette.removeColor': 'Supprimer la dernière couleur',
  'palette.customHelp': 'Choix exploratoire avancé. Vos couleurs ordonnées vont des valeurs faibles aux valeurs élevées ; l’uniformité perceptuelle et l’accessibilité aux déficiences de vision des couleurs ne sont pas garanties.',
  'palette.sequentialHelp': 'Des couleurs ordonnées perceptuellement préservent une progression fidèle des valeurs.',
  'palette.divergingHelp': 'Les couleurs divergentes montrent l’écart de part et d’autre d’un point central pertinent.',
  'palette.cvdHelp': 'Conçue pour rester lisible avec les déficiences courantes de la vision des couleurs.',
  'palette.nonUniformWarning': 'Choix avancé non analytique. Cette palette n’est pas perceptuellement uniforme : des écarts numériques égaux peuvent sembler différents.',
  'classes.title': 'Classes',
  'classes.mode': 'Méthode de classification',
  'classes.catalog': 'Défaut scientifique',
  'classes.advancedGroup': 'Avancé / exploratoire',
  'classes.adaptive': 'Adaptatif — tranche actuelle avancée',
  'classes.custom': 'Choisir le nombre — avancé',
  'classes.count': 'Classes demandées',
  'classes.catalogFixedHelp': 'Utilise les {actual} classes du catalogue et conserve la comparabilité des couleurs entre les périodes approuvées.',
  'classes.catalogFallbackHelp': 'Aucune échelle de référence fixe ne s’applique à cette tranche filtrée. Ses {actual} classes sont recalculées sur les données actuelles ; les couleurs ne sont pas comparables à une autre tranche.',
  'classes.adaptiveHelp': 'Mode exploratoire avancé : vise {target} classes selon les valeurs utilisables et distinctes ; les ex æquo ou contraintes scientifiques en donnent {actual}. Les mesures signées restent symétriques autour de leur référence, avec un nombre impair de classes. Les couleurs peuvent changer entre les sélections.',
  'classes.customHelp': 'Mode exploratoire avancé : demande {requested} classes ; les ex æquo ou contraintes scientifiques en donnent {actual}. Les mesures signées imposent un nombre impair de classes symétriques, donc le nombre réel peut différer. Les couleurs ne sont pas comparables entre les sélections.',
  'classes.adaptiveNote': 'Classification avancée de la tranche actuelle (cible {target} ; résultat {actual})',
  'classes.customNote': 'Classification avancée de la tranche actuelle ({requested} demandées ; résultat {actual})',
  'boundaries.title': 'Limites des zones',
  'boundaries.help': 'Afficher les limites sur la carte et dans les PNG téléchargés.',
  'charts.title': 'Graphique',
  'charts.mapView': 'Carte',
  'charts.tableView': 'Tableau',
  'charts.trend': 'Évolution dans le temps',
  'charts.composition': 'Composition',
  'charts.distribution': 'Distribution',
  'charts.comparison': 'Comparaison des entités',
  'charts.noData': 'Aucune donnée graphique n’est disponible pour cette sélection.',
  'charts.loading': 'Chargement des périodes comparables…',
  'charts.trendAria': 'Évolution de {metric} pour {area}',
  'charts.comparableOnly': 'L’axe vertical est ajusté aux valeurs affichées. Seules les périodes comparables sur le plan méthodologique sont reliées ; une série communale s’arrête lors d’une fusion ou d’un changement territorial vérifié.',
  'charts.period': 'Période',
  'charts.value': 'Valeur',
  'charts.pointCount': '{count} observations comparables.',
  'charts.tablePagination': 'Pages du tableau chronologique',
  'charts.tableRange': '{start}–{end} sur {count} observations',
  'metric.measureTitle': '{metric} : {measure}',
  'period.referenceDate': 'Date de référence : {date}',
  'period.observationDate': 'Date d’observation : {date}',
  'period.releaseDate': 'Date de publication : {date}',
  'period.serviceDate': 'Date de service programmée : {date}',
  'period.coveragePeriod': 'Période de couverture : {start} – {end}',
  'period.plannedSchedule': 'Horaire publié ; service planifié, pas opérations observées.',
  'boot.loading': 'Chargement des données officielles de la Belgique…',
  'boot.unavailable': 'Le catalogue de l’atlas est indisponible.',
  'error.catalogContract': 'Le catalogue publié ne respecte pas son contrat de données v2.',
  'error.catalogLoad': 'Le catalogue officiel n’a pas pu être chargé.',
  'error.catalogRequest': 'Échec de la requête du catalogue ({status}).',
  'error.boundaryRequest': 'Échec de la requête des limites ({status}).',
  'error.valuePackRequest': 'Échec de la requête du jeu de valeurs ({status}).',
  'error.boundaryCount': 'Nombre d’entités géographiques incorrect : {expected} attendues, {received} reçues.',
  'error.boundaryLoad': 'Le jeu de limites sélectionné n’a pas pu être chargé.',
  'error.valueContract': 'Le jeu de valeurs sélectionné ne respecte pas son contrat d’identité, de limites, de niveau ou de qualité.',
  'error.valuesLoad': 'Les valeurs sélectionnées n’ont pas pu être chargées.',
  'error.mapWebgl': 'WebGL n’a pas pu démarrer. Les mêmes données restent disponibles dans le tableau.',
  'error.mapRenderer': 'Le moteur de rendu WebGL a signalé une erreur.',
  'map.title': '{metric} par {geography}',
  'map.ariaLabel': 'Carte statistique interactive de la Belgique',
  'map.canvasLabel': 'Carte',
  'map.zoomIn': 'Zoom avant',
  'map.zoomOut': 'Zoom arrière',
  'map.areasDisplayed': '{count} entités de niveau « {level} » affichées.',
  'map.loading': 'Chargement de la carte.',
  'map.selectedDetails': 'Détails de l’entité sélectionnée',
  'map.closeSelected': 'Fermer l’entité sélectionnée',
  'map.provinceEquivalent': 'Bruxelles-Capitale est affichée comme équivalent provincial.',
  'map.viewDefinition': 'Voir la définition et la source →',
  'map.delta': '{delta} {unit} par rapport à {period}',
  'map.updating': 'Mise à jour de {count} entités…',
  'map.unavailable': 'Carte indisponible',
  'map.browseTable': 'Consulter les données sous forme de tableau',
  'map.methodology': 'Méthodologie',
  'map.selectedAnnouncement': '{name} sélectionnée. {value} {unit}, {period}.',
  'sidebar.closeMapControls': 'Fermer les commandes de la carte',
  'sidebar.mapControls': 'Commandes de la carte',
  'sidebar.closeControls': 'Fermer les commandes',
  'sidebar.officialData': 'Données officielles belges',
  'sidebar.boundaries': 'Limites {vintage}',
  'sidebar.heading': '{metric} par {geography}',
  'sidebar.measure': 'Mesure',
  'sidebar.geography': 'Géographie',
  'sidebar.levelUnavailable': '{level} — indisponible',
  'sidebar.provinceNote': 'Inclut Bruxelles-Capitale comme équivalent provincial afin de couvrir toute la Belgique.',
  'sidebar.period': 'Période',
  'sidebar.previousPeriod': 'Période précédente',
  'sidebar.nextPeriod': 'Période suivante',
  'sidebar.dataContext': 'Données : {label}',
  'sidebar.mapContext': 'Carte : {label}',
  'sidebar.onlyPeriod': 'Seule la période {period} est disponible pour cette géographie.',
  'sidebar.dataDetails': 'Détails des données',
  'sidebar.browseTable': 'Consulter le tableau',
  'level.region': 'Région',
  'level.province': 'Province + Bruxelles',
  'level.district': 'Arrondissement administratif',
  'level.municipality': 'Commune',
  'level.sector': 'Secteur statistique',
  'level.regionPlural': 'Régions',
  'level.provincePlural': 'Provinces + Bruxelles',
  'level.districtPlural': 'Arrondissements administratifs',
  'level.municipalityPlural': 'Communes',
  'level.sectorPlural': 'Secteurs statistiques',
  'indicator.label': 'Indicateur',
  'indicator.otherTopic': 'Autres indicateurs',
  'indicator.noneAvailable': 'Aucun indicateur disponible',
  'indicator.searchLabel': 'Rechercher des indicateurs',
  'indicator.searchPlaceholder': 'Rechercher un indicateur ou un thème',
  'indicator.countOne': '{count} indicateur',
  'indicator.countMany': '{count} indicateurs',
  'indicator.showMore': 'Afficher {count} de plus',
  'indicator.listLabel': 'Indicateurs',
  'indicator.current': 'Actuel',
  'indicator.noMatch': 'Aucun indicateur ne correspond à « {query} ».',
  'indicator.browseCategory': 'Parcourir par catégorie',
  'indicator.categoryFilter': 'Catégorie d’indicateurs',
  'indicator.clearCategory': 'Effacer le filtre de catégorie',
  'indicator.section.recent': 'Consultés récemment',
  'indicator.badgesLabel': 'Portée et type de source de l’indicateur',
  'indicator.scopeMixed': 'Portée mixte',
  'indicator.sourceType.officialRegister': 'Registre officiel',
  'indicator.sourceType.administrativeHealthcare': 'Données administratives de santé',
  'indicator.sourceType.surveyEstimate': 'Estimation d’enquête',
  'indicator.sourceType.scheduledService': 'Service programmé',
  'indicator.sourceType.remoteSensing': 'Dérivé de télédétection',
  'indicator.sourceType.modelled': 'Modélisé',
  'indicator.sourceType.europeanHarmonized': 'Harmonisé au niveau européen',
  'search.label': 'Rechercher un lieu ou un code officiel',
  'search.placeholder': 'Rechercher un lieu ou un code',
  'search.clear': 'Effacer la recherche',
  'search.noMatch': 'Aucune entité correspondante',
  'legend.label': 'Légende de la carte',
  'legend.classes': '{count} classes',
  'legend.class': '{count} classe',
  'legend.lessThan': '< {end}',
  'legend.atLeast': '≥ {start}',
  'legend.range': '{start} – < {end}',
  'legend.equal': '= {value}',
  'legend.closedRange': '{start} – {end}',
  'legend.fixedBreaks': 'Seuils {method} fixes{period}',
  'legend.periodSuffix': ' · {period}',
  'legend.areaCoverage': '{mapped} entités sur {total}',
  'legend.residentCoverage': ' · {percent} % des habitants cartographiés',
  'legend.statusCount': '{label} · {count}',
  'legend.scope': 'Portée : {scope}',
  'legend.symbols': 'Symboles proportionnels',
  'legend.symbolNote': 'La surface des cercles est proportionnelle à la valeur. Les aplats situent le contexte géographique.',
  'legend.symbolDistance': 'Écart par rapport à {center} : {distance}',
  'legend.symbolDistanceNote': 'La surface des cercles est proportionnelle à l’écart absolu par rapport à la valeur de référence {center}. Les aplats situent le contexte géographique.',
  'details.eyebrow': 'Méthodologie',
  'details.title': 'À propos de cette carte',
  'details.close': 'Fermer les détails des données',
  'details.measure': 'Mesure',
  'details.unit': 'Unité',
  'details.period': 'Période',
  'details.geography': 'Géographie',
  'details.boundaryPack': 'Jeu de limites',
  'details.geographyBasis': 'Base géographique',
  'details.basisNative': 'Limites historiques natives',
  'details.basisHarmonized': 'Géographie harmonisée',
  'details.basisMixed': 'Géographie rétrospective définie par la source',
  'details.coverageTitle': 'Couverture et classification',
  'details.mappedAreas': 'Entités cartographiées',
  'details.coverageScope': 'Portée des données',
  'details.comparisonScope': 'Portée de la comparaison',
  'details.rankingScope': 'Portée du classement',
  'details.rankingRegionalOnly': 'Uniquement dans la région déclarée',
  'details.scopeBelgium': 'Belgique',
  'details.scopeFlanders': 'Flandre',
  'details.scopeWallonia': 'Wallonie',
  'details.scopeBrussels': 'Bruxelles-Capitale',
  'details.scopeParticipating': 'Communes participantes',
  'details.scopeSourceDefined': 'Couverture définie par la source',
  'details.participatingAreas': 'Entités dans la portée',
  'details.outOfScope': 'Hors portée',
  'details.inScopeUnavailable': 'Indisponibles dans la portée',
  'details.residentCoverage': 'Couverture des habitants',
  'details.classMethod': 'Méthode de classification',
  'details.timeComparison': 'Comparaison temporelle',
  'details.comparisonEnabled': 'Activée pour cette série comparable',
  'details.comparisonByLineage': 'Activée tant que le territoire de la commune sélectionnée ne change pas ; une fusion crée une rupture',
  'details.comparisonDisabled': 'Non affichée pour des tranches incompatibles',
  'details.buildQa': 'Contrôles de qualité',
  'details.qaPassed': '{passed} / {total} contrôles réussis',
  'details.currentQuintiles': 'Quintiles de la tranche actuelle',
  'details.fixedClassification': 'Les seuils de classe sont fixes dans l’ensemble de comparaison déclaré pour cet indicateur, cette mesure et cette géographie.',
  'details.fallbackClassification': 'Aucune classification groupée n’a été publiée pour cette tranche ; la carte utilise donc les quintiles de la tranche actuelle.',
  'details.userClassification': 'Votre réglage recalcule les seuils sur la tranche actuelle. Il est utile pour l’exploration, mais les couleurs de la carte ne sont pas directement comparables entre les sélections ; les valeurs sources et les tendances approuvées restent inchangées.',
  'details.zeroMissing': 'Zéro reste une valeur valide ; les observations manquantes sont affichées séparément.',
  'details.geographicIntegrity': 'Intégrité géographique',
  'details.integrityCodes': 'Les codes NIS/REFNIS et les codes de secteurs statistiques sont traités comme des clés textuelles opaques. La construction utilise les champs explicites de Statbel pour le secteur, la commune, la province et la région ; elle ne déduit jamais une géographie parente en découpant un code. Bruxelles-Capitale est clairement identifiée comme équivalent provincial dans la vue des provinces.',
  'details.integrityBoundaries': 'Chaque tranche d’observations est liée à un jeu de limites immuable et associé à sa source. La carte change avec la période sélectionnée lorsque les secteurs statistiques ou les limites administratives de Statbel ont changé. Les écarts entre périodes ne sont affichés que lorsque le catalogue confirme explicitement la validité de la comparaison. Les enregistrements sectoriels non localisables sont signalés dans la couverture et ne sont conservés dans les totaux de niveau supérieur que lorsqu’une clé parente explicite existe.',
  'details.sources': 'Sources',
  'details.licencePrefix': 'Licences des sources publiées :',
  'details.licenceSuffix': 'Chaque transformation et opération géométrique est documentée dans les métadonnées de la source.',
  'details.reuseTermsNotExplicit': 'Distribution officielle publique ; aucun badge de licence explicite n’est fourni. Réutilisée avec attribution selon la politique du projet.',
  'details.sourceAccessed': 'Source consultée le :',
  'note.aimCodes': 'Les codes AIM/IMA {domain} ont été vérifiés et normalisés en codes REFNIS à cinq chiffres sans préfixe ; les libellés servent uniquement au rapprochement du tableau.',
  'note.biptExactCodes': 'Les codes NIS exacts de l’IBPT sont reliés au jeu annuel correspondant de limites Statbel ; aucune comparaison entre millésimes n’est déduite.',
  'note.fixedNutsGdp': 'Géographie NUTS 2024 fixe. Le PIB total de Verviers est la somme additive exacte de BE335 et BE336 ; sa valeur non additive de PIB par habitant est volontairement indisponible.',
  'note.exactMunicipalityCodes': 'Les codes communaux de la source sont reliés exactement au domaine officiel de limites contrôlé ; la hiérarchie parente provient des propriétés des entités géographiques.',
  'note.ghslFixedLau': 'Surface bâtie dérivée de la télédétection et modélisée sur les limites LAU GISCO 2020 fixes.',
  'note.brusselsProjection': 'Projection officielle limitée à Bruxelles sur le domaine fixe actuel de 19 communes ; les autres communes sont hors portée.',
  'note.sourceNativeDistrict': 'Observations par arrondissement dans la géographie native de la source ; aucune valeur d’un niveau supérieur n’est attribuée à une géographie plus fine.',
  'note.coordinateInventory': 'Inventaire dérivé de coordonnées ; zéro est une donnée valide et les points exclus restent non attribués.',
  'note.inventorySnapshot': 'Instantané de l’inventaire à la date de traitement des données source ; il ne s’agit pas d’une mesure de flux historique.',
  'note.gtfsCoordinates': 'Les coordonnées des arrêts GTFS sont reliées aux polygones communaux officiels dont le hachage a été vérifié ; aucun identifiant source n’est interprété comme une hiérarchie communale.',
  'note.regionalCurrentDomain': 'Domaine communal actuel — {scope} uniquement ; toutes les autres communes sont explicitement hors portée.',
  'note.sourceNativeMunicipality': 'Observations communales dans la géographie native de la source ; aucune valeur d’un niveau supérieur n’est attribuée à une géographie plus fine.',
  'note.clcIntersection': 'Polygones officiels CLC2018 intersectés avec les polygones communaux officiels Statbel 2018 dont le hachage a été vérifié, dans le système EPSG:3035.',
  'note.sparseCubeCoverage': 'La couverture du cube clairsemé est recalculée après l’application des filtres sélectionnés ; zéro reste une donnée valide.',
  'note.regionalCoverage': 'Les communes hors de la portée régionale déclarée sont explicitement indisponibles, et non statistiquement manquantes ; les statuts indisponible et masqué de l’éditeur restent distincts au sein de la portée.',
  'note.comparisonDeclaredGroup': 'La comparaison directe est limitée à ce groupe de comparaison déclaré et à ce jeu de limites immuable ; aucune correspondance entre limites n’est déduite.',
  'note.comparisonSourceDomain': 'Les comparaisons directes sont limitées au groupe de domaines de codes déclaré par la source.',
  'note.comparisonSingleSnapshot': 'Instantané unique au 1er janvier {year} sur le jeu de limites natif de {year}.',
  'note.comparisonMixedBases': 'Les écarts directs sont désactivés car l’ensemble de comparaison combine une classification de source et des bases de limites natives.',
  'note.comparisonBoundaryPacks': 'Les écarts directs sont désactivés car l’ensemble de comparaison traverse plusieurs jeux de limites natifs.',
  'note.comparisonAirFixed': 'Toutes les années utilisent la classification communale fixe postérieure à {year} d’IRCEL-CELINE.',
  'note.comparisonRegional': 'La comparaison et le classement sont limités aux {count} communes actuelles de {scope} ; les autres communes sont hors portée, et non des observations manquantes. Aucun classement belge n’est valide.',
  'note.comparisonEurostatGdp': 'Série nominale à prix courants sur une géographie NUTS 2024 fixe. Eurostat signale 2009 comme rupture et 2024 comme provisoire. Le classement par habitant couvre 42 arrondissements ; Verviers ne possède qu’un PIB total additif, car les deux ratios NUTS 3 ne sont pas agrégés.',
  'table.eyebrow': 'Vue accessible des données · {period}',
  'table.close': 'Fermer le tableau de données',
  'table.filterLabel': 'Filtrer les entités',
  'table.filterPlaceholder': 'Filtrer par entité ou par code',
  'table.areaCount': '{count} entités',
  'table.caption': '{title}, {period}.',
  'table.sortHint': 'Sélectionnez un en-tête de colonne pour trier ce tableau.',
  'table.sortedAscending': 'Trié par {column}, ordre croissant.',
  'table.sortedDescending': 'Trié par {column}, ordre décroissant.',
  'table.area': 'Entité',
  'table.officialCode': 'Code officiel',
  'table.previous': '← Précédente',
  'table.next': 'Suivante →',
  'table.page': 'Page {page} sur {count}',
}

const NL_MESSAGES: Record<MessageKey, string> = {
  'meta.title': 'België in kaart',
  'meta.description': 'Verken officiële Belgische statistieken op een transparante kaart met bronvermelding.',
  'language.label': 'Taal',
  'language.en': 'English',
  'language.fr': 'Français',
  'language.nl': 'Nederlands',
  'language.de': 'Deutsch',
  'brand.home': 'Startpagina van België in kaart',
  'brand.country': 'België',
  'brand.suffix': 'in kaart',
  'header.aboutData': 'Over de gegevens',
  'header.copyLink': 'Link kopiëren',
  'header.linkCopied': 'Link gekopieerd',
  'header.copyFallback': 'Kopieer de URL uit uw browser',
  'export.download': 'PNG downloaden',
  'export.resolution': 'PNG-resolutie',
  'export.twoX': '2× standaard',
  'export.threeX': '3× hoge resolutie',
  'export.preparing': 'PNG in hoge resolutie voorbereiden…',
  'export.preparingShort': 'Voorbereiden…',
  'export.complete': 'PNG gedownload.',
  'export.failed': 'De PNG kon niet worden gemaakt. Probeer opnieuw.',
  'export.filters': 'Filters',
  'export.scope': 'Kaartuitsnede',
  'export.scopeFull': 'Heel België',
  'export.scopeCurrent': 'Huidige kaartweergave',
  'export.scopeSelected': 'Geselecteerd gebied',
  'export.scopeSelectedUnavailable': 'Geselecteerd gebied (selecteer er eerst een)',
  'common.retry': 'Opnieuw proberen',
  'common.close': 'Sluiten',
  'common.noData': 'Geen gegevens',
  'common.suppressed': 'Onderdrukt of vertrouwelijk',
  'common.unavailable': 'Niet beschikbaar',
  'status.belowThreshold': 'Onder publicatiedrempel',
  'status.hidden': 'Verborgen door de uitgever',
  'status.notApplicable': 'Niet van toepassing',
  'status.missing': 'Door de uitgever als ontbrekend aangeduid',
  'status.empty': 'Lege broncel',
  'common.source': 'Bron',
  'common.officialDownload': 'Officiële download ↗',
  'common.notAvailable': '—',
  'common.percentagePointsShort': 'pp',
  'filters.title': 'Filters',
  'filters.all': 'Alle',
  'filters.clearAll': 'Filters wissen',
  'filters.selected': '{count} geselecteerd',
  'color.title': 'Kleurschaal',
  'color.palette': 'Palet',
  'color.sequential': 'Sequentieel',
  'color.diverging': 'Divergerend',
  'color.categorical': 'Categorisch',
  'color.fixedScale': 'Vaste vergelijkingsschaal',
  'color.currentScale': 'Schaal van de huidige selectie',
  'color.low': 'Laag',
  'color.high': 'Hoog',
  'color.midpoint': 'Referentiemiddelpunt',
  'color.missing': 'Ontbrekend',
  'color.suppressed': 'Onderdrukt of vertrouwelijk',
  'palette.default': 'Wetenschappelijke standaard',
  'palette.viridis': 'Viridis — geschikt bij kleurenblindheid',
  'palette.cividis': 'Cividis — geschikt bij kleurenblindheid',
  'palette.magma': 'Magma',
  'palette.turbo': 'Turbo — geavanceerd, niet-analytisch',
  'palette.jet': 'Jet — geavanceerd, niet-analytisch',
  'palette.blueRed': 'Blauw–rood',
  'palette.purpleGreen': 'Paars–groen',
  'palette.brownTeal': 'Bruin–blauwgroen',
  'palette.advancedGroup': 'Geavanceerd / verkennend',
  'palette.custom': 'Aangepast palet — geavanceerd',
  'palette.customColors': 'Geordende aangepaste kleuren',
  'palette.colorStop': 'Kleur {index}',
  'palette.addColor': 'Kleur toevoegen',
  'palette.removeColor': 'Laatste kleur verwijderen',
  'palette.customHelp': 'Geavanceerde verkennende keuze. Uw geordende kleuren lopen van laag naar hoog; perceptuele gelijkmatigheid en toegankelijkheid bij kleurenblindheid zijn niet gegarandeerd.',
  'palette.sequentialHelp': 'Perceptueel geordende kleuren behouden een eerlijke voortgang van laag naar hoog.',
  'palette.divergingHelp': 'Divergerende kleuren tonen de afstand aan weerszijden van een betekenisvol middelpunt.',
  'palette.cvdHelp': 'Ontworpen om leesbaar te blijven bij veelvoorkomende vormen van kleurenblindheid.',
  'palette.nonUniformWarning': 'Geavanceerde, niet-analytische keuze. Dit palet is perceptueel niet-uniform, waardoor gelijke datastappen ongelijk kunnen lijken.',
  'classes.title': 'Klassen',
  'classes.mode': 'Klassemethode',
  'classes.catalog': 'Wetenschappelijke standaard',
  'classes.advancedGroup': 'Geavanceerd / verkennend',
  'classes.adaptive': 'Adaptief — geavanceerde huidige doorsnede',
  'classes.custom': 'Aantal kiezen — geavanceerd',
  'classes.count': 'Gevraagde klassen',
  'classes.catalogFixedHelp': 'Gebruikt de {actual} catalogusklassen, zodat kleuren vergelijkbaar blijven over goedgekeurde perioden.',
  'classes.catalogFallbackHelp': 'Voor deze gefilterde doorsnede geldt geen vaste referentieschaal. De {actual} klassen worden opnieuw berekend op de huidige gegevens; kleuren zijn niet vergelijkbaar met een andere doorsnede.',
  'classes.adaptiveHelp': 'Geavanceerde verkennende modus: streeft naar {target} klassen op basis van bruikbare, unieke waarden; gelijke waarden of wetenschappelijke beperkingen leveren er {actual}. Maten met teken blijven symmetrisch rond hun referentie met een oneven aantal klassen. Kleuren kunnen per selectie veranderen.',
  'classes.customHelp': 'Geavanceerde verkennende modus: vraagt {requested} klassen; gelijke waarden of wetenschappelijke beperkingen leveren er {actual}. Maten met teken dwingen een symmetrisch oneven aantal klassen af, dus het werkelijke aantal kan afwijken. Kleuren zijn niet vergelijkbaar tussen selecties.',
  'classes.adaptiveNote': 'Geavanceerde classificatie van de huidige doorsnede (doel {target}; werkelijk {actual})',
  'classes.customNote': 'Geavanceerde classificatie van de huidige doorsnede ({requested} gevraagd; werkelijk {actual})',
  'boundaries.title': 'Gebiedsgrenzen',
  'boundaries.help': 'Toon grenslijnen op de kaart en in gedownloade PNG-bestanden.',
  'charts.title': 'Grafiek',
  'charts.mapView': 'Kaart',
  'charts.tableView': 'Tabel',
  'charts.trend': 'Trend in de tijd',
  'charts.composition': 'Samenstelling',
  'charts.distribution': 'Verdeling',
  'charts.comparison': 'Gebieden vergelijken',
  'charts.noData': 'Voor deze selectie zijn geen grafiekgegevens beschikbaar.',
  'charts.loading': 'Vergelijkbare perioden laden…',
  'charts.trendAria': 'Trend van {metric} voor {area}',
  'charts.comparableOnly': 'De verticale as is afgestemd op de getoonde waarden. Alleen methodologisch vergelijkbare perioden worden verbonden; een gemeentereeks stopt bij een gecontroleerde fusie of territoriale wijziging.',
  'charts.period': 'Periode',
  'charts.value': 'Waarde',
  'charts.pointCount': '{count} vergelijkbare waarnemingen.',
  'charts.tablePagination': 'Pagina’s van de trendtabel',
  'charts.tableRange': '{start}–{end} van {count} waarnemingen',
  'metric.measureTitle': '{metric}: {measure}',
  'period.referenceDate': 'Referentiedatum: {date}',
  'period.observationDate': 'Observatiedatum: {date}',
  'period.releaseDate': 'Publicatiedatum: {date}',
  'period.serviceDate': 'Geplande dienstdatum: {date}',
  'period.coveragePeriod': 'Dekkingsperiode: {start} – {end}',
  'period.plannedSchedule': 'Gepubliceerde dienstregeling; geplande dienst, geen waargenomen uitvoering.',
  'boot.loading': 'Officiële gegevens van België laden…',
  'boot.unavailable': 'De atlascatalogus is niet beschikbaar.',
  'error.catalogContract': 'De gepubliceerde catalogus voldoet niet aan het vastgelegde v2-gegevenscontract.',
  'error.catalogLoad': 'De officiële gegevenscatalogus kon niet worden geladen.',
  'error.catalogRequest': 'Catalogusaanvraag mislukt ({status}).',
  'error.boundaryRequest': 'Aanvraag van grenzen mislukt ({status}).',
  'error.valuePackRequest': 'Aanvraag van het waardenpakket mislukt ({status}).',
  'error.boundaryCount': 'Onjuist aantal grensobjecten: {expected} verwacht, {received} ontvangen.',
  'error.boundaryLoad': 'Het geselecteerde grenzenpakket kon niet worden geladen.',
  'error.valueContract': 'Het geselecteerde waardenpakket voldoet niet aan het contract voor identiteit, grenzen, niveau of kwaliteitscontrole.',
  'error.valuesLoad': 'De geselecteerde waarden konden niet worden geladen.',
  'error.mapWebgl': 'WebGL kon niet starten. Dezelfde gegevens blijven beschikbaar in de tabelweergave.',
  'error.mapRenderer': 'De WebGL-kaartweergave heeft een fout gemeld.',
  'map.title': '{metric} per {geography}',
  'map.ariaLabel': 'Interactieve statistische kaart van België',
  'map.canvasLabel': 'Kaart',
  'map.zoomIn': 'Inzoomen',
  'map.zoomOut': 'Uitzoomen',
  'map.areasDisplayed': '{count} gebieden op niveau {level} weergegeven.',
  'map.loading': 'Kaart laden.',
  'map.selectedDetails': 'Details van het geselecteerde gebied',
  'map.closeSelected': 'Geselecteerd gebied sluiten',
  'map.provinceEquivalent': 'Brussel-Hoofdstad wordt als provincie-equivalent weergegeven.',
  'map.viewDefinition': 'Definitie en bron bekijken →',
  'map.delta': '{delta} {unit} tegenover {period}',
  'map.updating': '{count} gebieden bijwerken…',
  'map.unavailable': 'Kaart niet beschikbaar',
  'map.browseTable': 'Gegevens als tabel bekijken',
  'map.methodology': 'Methodologie',
  'map.selectedAnnouncement': '{name} geselecteerd. {value} {unit}, {period}.',
  'sidebar.closeMapControls': 'Kaartbediening sluiten',
  'sidebar.mapControls': 'Kaartbediening',
  'sidebar.closeControls': 'Bediening sluiten',
  'sidebar.officialData': 'Officiële Belgische gegevens',
  'sidebar.boundaries': 'Grenzen {vintage}',
  'sidebar.heading': '{metric} per {geography}',
  'sidebar.measure': 'Maatstaf',
  'sidebar.geography': 'Geografie',
  'sidebar.levelUnavailable': '{level} — niet beschikbaar',
  'sidebar.provinceNote': 'Omvat Brussel-Hoofdstad als provincie-equivalent, zodat België volledig blijft.',
  'sidebar.period': 'Periode',
  'sidebar.previousPeriod': 'Vorige periode',
  'sidebar.nextPeriod': 'Volgende periode',
  'sidebar.dataContext': 'Gegevens: {label}',
  'sidebar.mapContext': 'Kaart: {label}',
  'sidebar.onlyPeriod': 'Alleen {period} is beschikbaar voor deze geografie.',
  'sidebar.dataDetails': 'Gegevensdetails',
  'sidebar.browseTable': 'Als tabel bekijken',
  'level.region': 'Gewest',
  'level.province': 'Provincie + Brussel',
  'level.district': 'Administratief arrondissement',
  'level.municipality': 'Gemeente',
  'level.sector': 'Statistische sector',
  'level.regionPlural': 'Gewesten',
  'level.provincePlural': 'Provincies + Brussel',
  'level.districtPlural': 'Administratieve arrondissementen',
  'level.municipalityPlural': 'Gemeenten',
  'level.sectorPlural': 'Statistische sectoren',
  'indicator.label': 'Indicator',
  'indicator.otherTopic': 'Andere indicatoren',
  'indicator.noneAvailable': 'Geen indicatoren beschikbaar',
  'indicator.searchLabel': 'Indicatoren zoeken',
  'indicator.searchPlaceholder': 'Indicatoren of thema’s zoeken',
  'indicator.countOne': '{count} indicator',
  'indicator.countMany': '{count} indicatoren',
  'indicator.showMore': 'Toon nog {count}',
  'indicator.listLabel': 'Indicatoren',
  'indicator.current': 'Huidig',
  'indicator.noMatch': 'Geen indicatoren gevonden voor ‘{query}’.',
  'indicator.browseCategory': 'Bladeren per categorie',
  'indicator.categoryFilter': 'Indicatorcategorie',
  'indicator.clearCategory': 'Categoriefilter wissen',
  'indicator.section.recent': 'Recent bekeken',
  'indicator.badgesLabel': 'Bereik en brontype van de indicator',
  'indicator.scopeMixed': 'Gemengd bereik',
  'indicator.sourceType.officialRegister': 'Officieel register',
  'indicator.sourceType.administrativeHealthcare': 'Administratieve gezondheidsdata',
  'indicator.sourceType.surveyEstimate': 'Enquêteschatting',
  'indicator.sourceType.scheduledService': 'Geplande dienst',
  'indicator.sourceType.remoteSensing': 'Afgeleid uit teledetectie',
  'indicator.sourceType.modelled': 'Gemodelleerd',
  'indicator.sourceType.europeanHarmonized': 'Europees geharmoniseerd',
  'search.label': 'Zoek een plaats of officiële code',
  'search.placeholder': 'Zoek plaats of code',
  'search.clear': 'Zoekopdracht wissen',
  'search.noMatch': 'Geen overeenkomend gebied',
  'legend.label': 'Kaartlegenda',
  'legend.classes': '{count} klassen',
  'legend.class': '{count} klasse',
  'legend.lessThan': '< {end}',
  'legend.atLeast': '≥ {start}',
  'legend.range': '{start} – < {end}',
  'legend.equal': '= {value}',
  'legend.closedRange': '{start} – {end}',
  'legend.fixedBreaks': 'Vaste {method}grenzen{period}',
  'legend.periodSuffix': ' · {period}',
  'legend.areaCoverage': '{mapped} van {total} gebieden',
  'legend.residentCoverage': ' · {percent}% van de inwoners in kaart',
  'legend.statusCount': '{label} · {count}',
  'legend.scope': 'Bereik: {scope}',
  'legend.symbols': 'Proportionele symbolen',
  'legend.symbolNote': 'De oppervlakte van elke cirkel is evenredig met de waarde. De vlakken tonen de geografische context.',
  'legend.symbolDistance': 'Afstand tot {center}: {distance}',
  'legend.symbolDistanceNote': 'De oppervlakte van elke cirkel is evenredig met de absolute afstand tot de referentiewaarde {center}. De vlakken tonen de geografische context.',
  'details.eyebrow': 'Methodologie',
  'details.title': 'Over deze kaart',
  'details.close': 'Gegevensdetails sluiten',
  'details.measure': 'Maatstaf',
  'details.unit': 'Eenheid',
  'details.period': 'Periode',
  'details.geography': 'Geografie',
  'details.boundaryPack': 'Grenzenpakket',
  'details.geographyBasis': 'Geografische basis',
  'details.basisNative': 'Oorspronkelijke historische grenzen',
  'details.basisHarmonized': 'Geharmoniseerde geografie',
  'details.basisMixed': 'Door de bron bepaalde retrospectieve geografie',
  'details.coverageTitle': 'Dekking en classificatie',
  'details.mappedAreas': 'Gebieden met gegevens',
  'details.coverageScope': 'Gegevensbereik',
  'details.comparisonScope': 'Vergelijkingsbereik',
  'details.rankingScope': 'Rangschikkingsbereik',
  'details.rankingRegionalOnly': 'Alleen binnen het opgegeven gewest',
  'details.scopeBelgium': 'België',
  'details.scopeFlanders': 'Vlaanderen',
  'details.scopeWallonia': 'Wallonië',
  'details.scopeBrussels': 'Brussel-Hoofdstad',
  'details.scopeParticipating': 'Deelnemende gemeenten',
  'details.scopeSourceDefined': 'Door de bron bepaald bereik',
  'details.participatingAreas': 'Gebieden binnen het bereik',
  'details.outOfScope': 'Buiten bereik',
  'details.inScopeUnavailable': 'Niet beschikbaar binnen het bereik',
  'details.residentCoverage': 'Dekking van inwoners',
  'details.classMethod': 'Klassenmethode',
  'details.timeComparison': 'Vergelijking in de tijd',
  'details.comparisonEnabled': 'Ingeschakeld voor deze vergelijkbare reeks',
  'details.comparisonByLineage': 'Ingeschakeld zolang het grondgebied van de gekozen gemeente ongewijzigd blijft; een fusie onderbreekt de reeks',
  'details.comparisonDisabled': 'Niet getoond voor onverenigbare doorsneden',
  'details.buildQa': 'Kwaliteitscontroles',
  'details.qaPassed': '{passed} / {total} controles geslaagd',
  'details.currentQuintiles': 'Kwantielen van de huidige doorsnede',
  'details.fixedClassification': 'De klassengrenzen liggen vast binnen de opgegeven vergelijkingsset voor deze indicator, maatstaf en geografie.',
  'details.fallbackClassification': 'Voor deze doorsnede is geen gepoolde classificatie gepubliceerd. De kaart gebruikt daarom de kwantielen van de huidige doorsnede.',
  'details.userClassification': 'Uw klasse-instelling berekent grenzen opnieuw uit de huidige doorsnede. Dit is nuttig voor verkenning, maar kaartkleuren zijn niet rechtstreeks vergelijkbaar tussen selecties; bronwaarden en goedgekeurde trends blijven ongewijzigd.',
  'details.zeroMissing': 'Nul blijft een geldige waarde; ontbrekende waarnemingen worden afzonderlijk weergegeven.',
  'details.geographicIntegrity': 'Geografische integriteit',
  'details.integrityCodes': 'NIS-/REFNIS-codes en codes van statistische sectoren worden behandeld als niet-opdeelbare tekstsleutels. De opbouw koppelt de expliciete velden van Statbel voor sector, gemeente, provincie en gewest; een bovenliggende geografie wordt nooit afgeleid door een code op te splitsen. Brussel-Hoofdstad is in de provincieweergave duidelijk aangeduid als provincie-equivalent.',
  'details.integrityBoundaries': 'Elke waarnemingsdoorsnede is gekoppeld aan een onveranderlijk grenzenpakket met vastgelegde bron. De kaart verandert met de geselecteerde periode wanneer de statistische sectoren of administratieve grenzen van Statbel wijzigden. Verschillen tussen perioden worden alleen getoond wanneer de catalogus de vergelijking uitdrukkelijk als geldig aanduidt. Niet-lokaliseerbare sectorrecords worden vermeld in de dekking en alleen in totalen op een hoger niveau behouden wanneer een expliciete bovenliggende sleutel beschikbaar is.',
  'details.sources': 'Bronnen',
  'details.licencePrefix': 'Gepubliceerde bronlicenties:',
  'details.licenceSuffix': 'Elke transformatie en geometrische bewerking is gedocumenteerd in de bronmetadata.',
  'details.reuseTermsNotExplicit': 'Publieke officiële distributie; er werd geen expliciet licentielabel verstrekt. Hergebruikt met bronvermelding volgens het projectbeleid.',
  'details.sourceAccessed': 'Bron geraadpleegd op:',
  'note.aimCodes': 'AIM/IMA-codes voor {domain} zijn gecontroleerd en genormaliseerd tot kale REFNIS-codes van vijf cijfers; labels worden alleen gebruikt voor afstemming met de tabel.',
  'note.biptExactCodes': 'Exacte BIPT-NIS-codes worden gekoppeld aan het overeenkomende jaarlijkse grenzenpakket van Statbel; er wordt geen vergelijking tussen jaargangen afgeleid.',
  'note.fixedNutsGdp': 'Vaste NUTS 2024-geografie. Het totale bbp van Verviers is de exacte additieve som van BE335 en BE336; de niet-additieve waarde van het bbp per inwoner is bewust niet beschikbaar.',
  'note.exactMunicipalityCodes': 'Gemeentecodes uit de bron worden exact gekoppeld aan het gecontroleerde officiële grensdomein; de bovenliggende hiërarchie komt uit de eigenschappen van de grensobjecten.',
  'note.ghslFixedLau': 'Uit teledetectie afgeleid en gemodelleerd bebouwd oppervlak op vaste GISCO 2020-LAU-grenzen.',
  'note.brusselsProjection': 'Officiële projectie alleen voor Brussel op het vaste huidige domein van 19 gemeenten; andere gemeenten vallen buiten het bereik.',
  'note.sourceNativeDistrict': 'Arrondissementswaarnemingen in de brongeografie; een waarde op hoger niveau wordt niet aan een fijnere geografie toegewezen.',
  'note.coordinateInventory': 'Inventaris afgeleid uit coördinaten; nul is geldige data en uitgesloten punten blijven niet toegewezen.',
  'note.inventorySnapshot': 'Momentopname van de inventaris op de verwerkingsdatum van de brongegevens; dit is geen historische stroommaatstaf.',
  'note.gtfsCoordinates': 'GTFS-haltecoördinaten worden gekoppeld aan officiële gemeentepolygonen met gecontroleerde hash; geen bronidentificatie wordt als gemeentehiërarchie geïnterpreteerd.',
  'note.regionalCurrentDomain': 'Huidig gemeentelijk domein uitsluitend voor {scope}; alle andere gemeenten vallen expliciet buiten het bereik.',
  'note.sourceNativeMunicipality': 'Gemeentewaarnemingen in de brongeografie; een waarde op hoger niveau wordt niet aan een fijnere geografie toegewezen.',
  'note.clcIntersection': 'Officiële CLC2018-polygonen doorsneden met officiële Statbel-gemeentepolygonen van 2018 met gecontroleerde hash, in EPSG:3035.',
  'note.sparseCubeCoverage': 'De dekking van de schaarse cube wordt herberekend nadat de geselecteerde filters zijn toegepast; nul blijft geldige data.',
  'note.regionalCoverage': 'Gemeenten buiten het opgegeven regionale bereik zijn expliciet niet beschikbaar en niet statistisch ontbrekend; door de uitgever als niet beschikbaar of onderdrukt aangeduide statussen blijven binnen het bereik afzonderlijk.',
  'note.comparisonDeclaredGroup': 'Rechtstreekse vergelijking is beperkt tot deze opgegeven vergelijkingsgroep en dit onveranderlijke grenzenpakket; er wordt geen overeenkomst tussen grenzen afgeleid.',
  'note.comparisonSourceDomain': 'Rechtstreekse vergelijkingen zijn beperkt tot de door de bron opgegeven codedomeingroep.',
  'note.comparisonSingleSnapshot': 'Eén momentopname op 1 januari {year} op het oorspronkelijke grenzenpakket van {year}.',
  'note.comparisonMixedBases': 'Rechtstreekse verschillen zijn uitgeschakeld omdat de vergelijkingsset bronclassificatie en oorspronkelijke grensbasissen combineert.',
  'note.comparisonBoundaryPacks': 'Rechtstreekse verschillen zijn uitgeschakeld omdat de vergelijkingsset meerdere oorspronkelijke grenzenpakketten overspant.',
  'note.comparisonAirFixed': 'Alle jaren gebruiken de vaste gemeentelijke classificatie van IRCEL-CELINE van na {year}.',
  'note.comparisonRegional': 'Vergelijking en rangschikking zijn beperkt tot de {count} huidige gemeenten in {scope}; gemeenten elders vallen buiten het bereik en zijn geen ontbrekende waarnemingen. Een rangschikking voor heel België is niet geldig.',
  'note.comparisonEurostatGdp': 'Nominale reeks tegen lopende prijzen op een vaste NUTS 2024-geografie. Eurostat markeert 2009 als breuk en 2024 als voorlopig. De rangschikking per inwoner omvat 42 arrondissementen; Verviers heeft alleen een additief totaal bbp omdat de twee NUTS 3-ratio’s niet worden samengevoegd.',
  'table.eyebrow': 'Toegankelijke gegevensweergave · {period}',
  'table.close': 'Gegevenstabel sluiten',
  'table.filterLabel': 'Gebieden filteren',
  'table.filterPlaceholder': 'Filter op gebied of code',
  'table.areaCount': '{count} gebieden',
  'table.caption': '{title}, {period}.',
  'table.sortHint': 'Selecteer een kolomkop om deze tabel te sorteren.',
  'table.sortedAscending': 'Gesorteerd op {column}, oplopend.',
  'table.sortedDescending': 'Gesorteerd op {column}, aflopend.',
  'table.area': 'Gebied',
  'table.officialCode': 'Officiële code',
  'table.previous': '← Vorige',
  'table.next': 'Volgende →',
  'table.page': 'Pagina {page} van {count}',
}

const DE_MESSAGES: Record<MessageKey, string> = {
  'meta.title': 'Belgien in Karten',
  'meta.description': 'Entdecken Sie offizielle belgische Statistiken auf einer transparenten Karte mit Quellenangaben.',
  'language.label': 'Sprache',
  'language.en': 'English',
  'language.fr': 'Français',
  'language.nl': 'Nederlands',
  'language.de': 'Deutsch',
  'brand.home': 'Startseite von Belgien in Karten',
  'brand.country': 'Belgien',
  'brand.suffix': 'in Karten',
  'header.aboutData': 'Über die Daten',
  'header.copyLink': 'Link kopieren',
  'header.linkCopied': 'Link kopiert',
  'header.copyFallback': 'Kopieren Sie die URL aus Ihrem Browser',
  'export.download': 'PNG herunterladen',
  'export.resolution': 'PNG-Auflösung',
  'export.twoX': '2× Standard',
  'export.threeX': '3× hohe Auflösung',
  'export.preparing': 'Hochauflösendes PNG wird erstellt…',
  'export.preparingShort': 'Wird erstellt…',
  'export.complete': 'PNG heruntergeladen.',
  'export.failed': 'Das PNG konnte nicht erstellt werden. Erneut versuchen.',
  'export.filters': 'Filter',
  'export.scope': 'Kartenausschnitt',
  'export.scopeFull': 'Ganz Belgien',
  'export.scopeCurrent': 'Aktuelle Kartenansicht',
  'export.scopeSelected': 'Ausgewähltes Gebiet',
  'export.scopeSelectedUnavailable': 'Ausgewähltes Gebiet (zuerst auswählen)',
  'common.retry': 'Erneut versuchen',
  'common.close': 'Schließen',
  'common.noData': 'Keine Daten',
  'common.suppressed': 'Unterdrückt oder vertraulich',
  'common.unavailable': 'Nicht verfügbar',
  'status.belowThreshold': 'Unter Veröffentlichungsschwelle',
  'status.hidden': 'Vom Herausgeber ausgeblendet',
  'status.notApplicable': 'Nicht anwendbar',
  'status.missing': 'Vom Herausgeber als fehlend gekennzeichnet',
  'status.empty': 'Leere Quellzelle',
  'common.source': 'Quelle',
  'common.officialDownload': 'Offizieller Download ↗',
  'common.notAvailable': '—',
  'common.percentagePointsShort': 'PP',
  'filters.title': 'Filter',
  'filters.all': 'Alle',
  'filters.clearAll': 'Filter zurücksetzen',
  'filters.selected': '{count} ausgewählt',
  'color.title': 'Farbskala',
  'color.palette': 'Palette',
  'color.sequential': 'Sequenziell',
  'color.diverging': 'Divergierend',
  'color.categorical': 'Kategorisch',
  'color.fixedScale': 'Feste Vergleichsskala',
  'color.currentScale': 'Skala der aktuellen Auswahl',
  'color.low': 'Niedrig',
  'color.high': 'Hoch',
  'color.midpoint': 'Referenzmittelpunkt',
  'color.missing': 'Fehlend',
  'color.suppressed': 'Unterdrückt oder vertraulich',
  'palette.default': 'Wissenschaftlicher Standard',
  'palette.viridis': 'Viridis — farbfehlsichtigkeitsgeeignet',
  'palette.cividis': 'Cividis — farbfehlsichtigkeitsgeeignet',
  'palette.magma': 'Magma',
  'palette.turbo': 'Turbo — erweitert, nicht analytisch',
  'palette.jet': 'Jet — erweitert, nicht analytisch',
  'palette.blueRed': 'Blau–Rot',
  'palette.purpleGreen': 'Violett–Grün',
  'palette.brownTeal': 'Braun–Blaugrün',
  'palette.advancedGroup': 'Erweitert / explorativ',
  'palette.custom': 'Eigene Palette — erweitert',
  'palette.customColors': 'Geordnete eigene Farben',
  'palette.colorStop': 'Farbe {index}',
  'palette.addColor': 'Farbe hinzufügen',
  'palette.removeColor': 'Letzte Farbe entfernen',
  'palette.customHelp': 'Erweiterte explorative Wahl. Ihre geordneten Farben verlaufen von niedrigen zu hohen Werten; Wahrnehmungsgleichheit und Barrierefreiheit bei Farbfehlsichtigkeit sind nicht garantiert.',
  'palette.sequentialHelp': 'Wahrnehmungsgetreu geordnete Farben bewahren einen ehrlichen Verlauf von niedrig nach hoch.',
  'palette.divergingHelp': 'Divergierende Farben zeigen den Abstand auf beiden Seiten eines aussagekräftigen Mittelpunkts.',
  'palette.cvdHelp': 'Bleibt bei häufigen Formen von Farbfehlsichtigkeit gut lesbar.',
  'palette.nonUniformWarning': 'Erweiterte, nicht analytische Wahl. Diese Palette ist wahrnehmungsmäßig nicht gleichmäßig, sodass gleiche Datenschritte ungleich wirken können.',
  'classes.title': 'Klassen',
  'classes.mode': 'Klassenmethode',
  'classes.catalog': 'Wissenschaftlicher Standard',
  'classes.advancedGroup': 'Erweitert / explorativ',
  'classes.adaptive': 'Adaptiv — erweiterter aktueller Ausschnitt',
  'classes.custom': 'Anzahl wählen — erweitert',
  'classes.count': 'Gewünschte Klassen',
  'classes.catalogFixedHelp': 'Verwendet die {actual} Katalogklassen, sodass Farben über freigegebene Zeiträume vergleichbar bleiben.',
  'classes.catalogFallbackHelp': 'Für diesen gefilterten Ausschnitt gilt keine feste Referenzskala. Seine {actual} Klassen werden aus den aktuellen Daten neu berechnet; Farben sind nicht mit einem anderen Ausschnitt vergleichbar.',
  'classes.adaptiveHelp': 'Erweiterter explorativer Modus: zielt anhand nutzbarer, unterschiedlicher Werte auf {target} Klassen; Bindungen oder wissenschaftliche Vorgaben ergeben {actual}. Vorzeichenbehaftete Maße bleiben mit ungerader Klassenzahl symmetrisch um ihre Referenz. Farben können sich zwischen Auswahlen ändern.',
  'classes.customHelp': 'Erweiterter explorativer Modus: fordert {requested} Klassen an; Bindungen oder wissenschaftliche Vorgaben ergeben {actual}. Vorzeichenbehaftete Maße erzwingen eine symmetrische ungerade Klassenzahl, daher kann die tatsächliche Zahl abweichen. Farben sind zwischen Auswahlen nicht vergleichbar.',
  'classes.adaptiveNote': 'Erweiterte Klassifikation des aktuellen Ausschnitts (Ziel {target}; tatsächlich {actual})',
  'classes.customNote': 'Erweiterte Klassifikation des aktuellen Ausschnitts ({requested} angefordert; tatsächlich {actual})',
  'boundaries.title': 'Gebietsgrenzen',
  'boundaries.help': 'Grenzlinien auf der Karte und in PNG-Downloads anzeigen.',
  'charts.title': 'Diagramm',
  'charts.mapView': 'Karte',
  'charts.tableView': 'Tabelle',
  'charts.trend': 'Zeitlicher Verlauf',
  'charts.composition': 'Zusammensetzung',
  'charts.distribution': 'Verteilung',
  'charts.comparison': 'Gebietsvergleich',
  'charts.noData': 'Für diese Auswahl sind keine Diagrammdaten verfügbar.',
  'charts.loading': 'Vergleichbare Zeiträume werden geladen…',
  'charts.trendAria': 'Zeitverlauf von {metric} für {area}',
  'charts.comparableOnly': 'Die vertikale Achse ist an die angezeigten Werte angepasst. Verbunden werden nur methodisch vergleichbare Zeiträume; eine Gemeindereihe endet bei einer geprüften Fusion oder Gebietsänderung.',
  'charts.period': 'Zeitraum',
  'charts.value': 'Wert',
  'charts.pointCount': '{count} vergleichbare Beobachtungen.',
  'charts.tablePagination': 'Seiten der Verlaufstabelle',
  'charts.tableRange': '{start}–{end} von {count} Beobachtungen',
  'metric.measureTitle': '{metric}: {measure}',
  'period.referenceDate': 'Stichtag: {date}',
  'period.observationDate': 'Beobachtungsdatum: {date}',
  'period.releaseDate': 'Veröffentlichungsdatum: {date}',
  'period.serviceDate': 'Geplantes Betriebsdatum: {date}',
  'period.coveragePeriod': 'Abdeckungszeitraum: {start} – {end}',
  'period.plannedSchedule': 'Veröffentlichter Fahrplan; geplantes Angebot, kein beobachteter Betrieb.',
  'boot.loading': 'Offizielle Daten Belgiens werden geladen…',
  'boot.unavailable': 'Der Atlaskatalog ist nicht verfügbar.',
  'error.catalogContract': 'Der veröffentlichte Katalog erfüllt den angegebenen v2-Datenvertrag nicht.',
  'error.catalogLoad': 'Der offizielle Datenkatalog konnte nicht geladen werden.',
  'error.catalogRequest': 'Kataloganfrage fehlgeschlagen ({status}).',
  'error.boundaryRequest': 'Anfrage der Grenzen fehlgeschlagen ({status}).',
  'error.valuePackRequest': 'Anfrage des Wertepakets fehlgeschlagen ({status}).',
  'error.boundaryCount': 'Falsche Anzahl an Grenzobjekten: {expected} erwartet, {received} empfangen.',
  'error.boundaryLoad': 'Das ausgewählte Grenzenpaket konnte nicht geladen werden.',
  'error.valueContract': 'Das ausgewählte Wertepaket erfüllt seinen Vertrag zu Identität, Grenzen, Ebene oder Qualitätsprüfung nicht.',
  'error.valuesLoad': 'Die ausgewählten Werte konnten nicht geladen werden.',
  'error.mapWebgl': 'WebGL konnte nicht gestartet werden. Dieselben Daten bleiben in der Tabellenansicht verfügbar.',
  'error.mapRenderer': 'Die WebGL-Kartendarstellung hat einen Fehler gemeldet.',
  'map.title': '{metric} nach {geography}',
  'map.ariaLabel': 'Interaktive statistische Karte von Belgien',
  'map.canvasLabel': 'Karte',
  'map.zoomIn': 'Vergrößern',
  'map.zoomOut': 'Verkleinern',
  'map.areasDisplayed': '{count} Gebiete der Ebene {level} angezeigt.',
  'map.loading': 'Karte wird geladen.',
  'map.selectedDetails': 'Details zum ausgewählten Gebiet',
  'map.closeSelected': 'Ausgewähltes Gebiet schließen',
  'map.provinceEquivalent': 'Brüssel-Hauptstadt wird als Provinzäquivalent dargestellt.',
  'map.viewDefinition': 'Definition und Quelle anzeigen →',
  'map.delta': '{delta} {unit} gegenüber {period}',
  'map.updating': '{count} Gebiete werden aktualisiert…',
  'map.unavailable': 'Karte nicht verfügbar',
  'map.browseTable': 'Daten als Tabelle anzeigen',
  'map.methodology': 'Methodik',
  'map.selectedAnnouncement': '{name} ausgewählt. {value} {unit}, {period}.',
  'sidebar.closeMapControls': 'Kartensteuerung schließen',
  'sidebar.mapControls': 'Kartensteuerung',
  'sidebar.closeControls': 'Steuerung schließen',
  'sidebar.officialData': 'Offizielle belgische Daten',
  'sidebar.boundaries': 'Grenzen {vintage}',
  'sidebar.heading': '{metric} nach {geography}',
  'sidebar.measure': 'Kennzahl',
  'sidebar.geography': 'Geografie',
  'sidebar.levelUnavailable': '{level} — nicht verfügbar',
  'sidebar.provinceNote': 'Schließt Brüssel-Hauptstadt als Provinzäquivalent ein, damit Belgien vollständig abgedeckt ist.',
  'sidebar.period': 'Zeitraum',
  'sidebar.previousPeriod': 'Vorheriger Zeitraum',
  'sidebar.nextPeriod': 'Nächster Zeitraum',
  'sidebar.dataContext': 'Daten: {label}',
  'sidebar.mapContext': 'Karte: {label}',
  'sidebar.onlyPeriod': 'Auf dieser geografischen Ebene ist nur {period} verfügbar.',
  'sidebar.dataDetails': 'Datendetails',
  'sidebar.browseTable': 'Als Tabelle anzeigen',
  'level.region': 'Region',
  'level.province': 'Provinz + Brüssel',
  'level.district': 'Verwaltungsbezirk',
  'level.municipality': 'Gemeinde',
  'level.sector': 'Statistischer Sektor',
  'level.regionPlural': 'Regionen',
  'level.provincePlural': 'Provinzen + Brüssel',
  'level.districtPlural': 'Verwaltungsbezirke',
  'level.municipalityPlural': 'Gemeinden',
  'level.sectorPlural': 'Statistische Sektoren',
  'indicator.label': 'Indikator',
  'indicator.otherTopic': 'Weitere Indikatoren',
  'indicator.noneAvailable': 'Keine Indikatoren verfügbar',
  'indicator.searchLabel': 'Indikatoren suchen',
  'indicator.searchPlaceholder': 'Indikatoren oder Themen suchen',
  'indicator.countOne': '{count} Indikator',
  'indicator.countMany': '{count} Indikatoren',
  'indicator.showMore': '{count} weitere anzeigen',
  'indicator.listLabel': 'Indikatoren',
  'indicator.current': 'Aktuell',
  'indicator.noMatch': 'Keine Indikatoren entsprechen „{query}“.',
  'indicator.browseCategory': 'Nach Kategorie durchsuchen',
  'indicator.categoryFilter': 'Indikatorkategorie',
  'indicator.clearCategory': 'Kategoriefilter löschen',
  'indicator.section.recent': 'Zuletzt angesehen',
  'indicator.badgesLabel': 'Geltungsbereich und Quellentyp des Indikators',
  'indicator.scopeMixed': 'Gemischter Geltungsbereich',
  'indicator.sourceType.officialRegister': 'Amtliches Register',
  'indicator.sourceType.administrativeHealthcare': 'Administrative Gesundheitsdaten',
  'indicator.sourceType.surveyEstimate': 'Umfrageschätzung',
  'indicator.sourceType.scheduledService': 'Planmäßiges Angebot',
  'indicator.sourceType.remoteSensing': 'Aus Fernerkundung abgeleitet',
  'indicator.sourceType.modelled': 'Modelliert',
  'indicator.sourceType.europeanHarmonized': 'Europäisch harmonisiert',
  'search.label': 'Ort oder offiziellen Code suchen',
  'search.placeholder': 'Ort oder Code suchen',
  'search.clear': 'Suche löschen',
  'search.noMatch': 'Kein passendes Gebiet',
  'legend.label': 'Kartenlegende',
  'legend.classes': '{count} Klassen',
  'legend.class': '{count} Klasse',
  'legend.lessThan': '< {end}',
  'legend.atLeast': '≥ {start}',
  'legend.range': '{start} – < {end}',
  'legend.equal': '= {value}',
  'legend.closedRange': '{start} – {end}',
  'legend.fixedBreaks': 'Feste {method}-Grenzen{period}',
  'legend.periodSuffix': ' · {period}',
  'legend.areaCoverage': '{mapped} von {total} Gebieten',
  'legend.residentCoverage': ' · {percent}% der Einwohner erfasst',
  'legend.statusCount': '{label} · {count}',
  'legend.scope': 'Geltungsbereich: {scope}',
  'legend.symbols': 'Proportionale Symbole',
  'legend.symbolNote': 'Die Kreisfläche ist proportional zum Wert. Die Flächenfüllung zeigt den geografischen Kontext.',
  'legend.symbolDistance': 'Abstand zu {center}: {distance}',
  'legend.symbolDistanceNote': 'Die Kreisfläche ist proportional zum absoluten Abstand vom Referenzwert {center}. Die Flächenfüllung zeigt den geografischen Kontext.',
  'details.eyebrow': 'Methodik',
  'details.title': 'Über diese Karte',
  'details.close': 'Datendetails schließen',
  'details.measure': 'Kennzahl',
  'details.unit': 'Einheit',
  'details.period': 'Zeitraum',
  'details.geography': 'Geografie',
  'details.boundaryPack': 'Grenzenpaket',
  'details.geographyBasis': 'Geografische Basis',
  'details.basisNative': 'Ursprüngliche historische Grenzen',
  'details.basisHarmonized': 'Harmonisierte Geografie',
  'details.basisMixed': 'Von der Quelle definierte retrospektive Geografie',
  'details.coverageTitle': 'Abdeckung und Klassifikation',
  'details.mappedAreas': 'Kartierte Gebiete',
  'details.coverageScope': 'Datenbereich',
  'details.comparisonScope': 'Vergleichsbereich',
  'details.rankingScope': 'Ranglistenbereich',
  'details.rankingRegionalOnly': 'Nur innerhalb der angegebenen Region',
  'details.scopeBelgium': 'Belgien',
  'details.scopeFlanders': 'Flandern',
  'details.scopeWallonia': 'Wallonien',
  'details.scopeBrussels': 'Brüssel-Hauptstadt',
  'details.scopeParticipating': 'Teilnehmende Gemeinden',
  'details.scopeSourceDefined': 'Von der Quelle definierter Bereich',
  'details.participatingAreas': 'Gebiete im Geltungsbereich',
  'details.outOfScope': 'Außerhalb des Geltungsbereichs',
  'details.inScopeUnavailable': 'Im Geltungsbereich nicht verfügbar',
  'details.residentCoverage': 'Einwohnerabdeckung',
  'details.classMethod': 'Klassenmethode',
  'details.timeComparison': 'Zeitvergleich',
  'details.comparisonEnabled': 'Für diese vergleichbare Reihe aktiviert',
  'details.comparisonByLineage': 'Aktiviert, solange das Gebiet der gewählten Gemeinde unverändert bleibt; eine Fusion unterbricht die Reihe',
  'details.comparisonDisabled': 'Bei inkompatiblen Ausschnitten nicht angezeigt',
  'details.buildQa': 'Qualitätsprüfung',
  'details.qaPassed': '{passed} / {total} Prüfungen bestanden',
  'details.currentQuintiles': 'Quantile des aktuellen Ausschnitts',
  'details.fixedClassification': 'Die Klassengrenzen sind innerhalb der angegebenen Vergleichsgruppe für diesen Indikator, diese Kennzahl und diese Geografie festgelegt.',
  'details.fallbackClassification': 'Für diesen Ausschnitt wurde keine gepoolte Klassifikation veröffentlicht. Daher verwendet die Karte die Quantile des aktuellen Ausschnitts.',
  'details.userClassification': 'Ihre Klasseneinstellung berechnet Grenzen aus dem aktuellen Ausschnitt neu. Das ist für Erkundungen nützlich, Kartenfarben sind zwischen Auswahlen jedoch nicht direkt vergleichbar; Quellwerte und freigegebene Trends bleiben unverändert.',
  'details.zeroMissing': 'Null bleibt ein gültiger Wert; fehlende Beobachtungen werden getrennt dargestellt.',
  'details.geographicIntegrity': 'Geografische Integrität',
  'details.integrityCodes': 'NIS-/REFNIS-Codes und Codes statistischer Sektoren werden als nicht teilbare Textschlüssel behandelt. Der Aufbau verknüpft die ausdrücklichen Felder von Statbel für Sektor, Gemeinde, Provinz und Region; eine übergeordnete Geografie wird nie durch das Aufteilen eines Codes abgeleitet. Brüssel-Hauptstadt ist in der Provinzansicht eindeutig als Provinzäquivalent gekennzeichnet.',
  'details.integrityBoundaries': 'Jeder Beobachtungsausschnitt ist an ein unveränderliches, mit der Quelle verknüpftes Grenzenpaket gebunden. Die Karte wechselt mit dem ausgewählten Zeitraum, wenn sich die statistischen Sektoren oder Verwaltungsgrenzen von Statbel geändert haben. Unterschiede zwischen Zeiträumen werden nur angezeigt, wenn der Katalog den Vergleich ausdrücklich als gültig kennzeichnet. Nicht lokalisierbare Sektordatensätze werden in der Abdeckung offengelegt und nur dann in Summen höherer Ebenen beibehalten, wenn ein ausdrücklicher übergeordneter Schlüssel verfügbar ist.',
  'details.sources': 'Quellen',
  'details.licencePrefix': 'Veröffentlichte Quellenlizenzen:',
  'details.licenceSuffix': 'Jede Transformation und Geometrieverarbeitung ist in den Quellenmetadaten dokumentiert.',
  'details.reuseTermsNotExplicit': 'Öffentliche amtliche Distribution; kein ausdrücklicher Lizenzhinweis wurde bereitgestellt. Wiederverwendet mit Quellenangabe gemäß der Projektrichtlinie.',
  'details.sourceAccessed': 'Quelle abgerufen am:',
  'note.aimCodes': 'AIM/IMA-Codes für {domain} wurden geprüft und auf reine fünfstellige REFNIS-Codes normalisiert; Bezeichnungen dienen nur dem Tabellenabgleich.',
  'note.biptExactCodes': 'Exakte BIPT-NIS-Codes werden mit dem passenden jährlichen Statbel-Grenzenpaket verknüpft; ein Vergleich zwischen Jahrgängen wird nicht abgeleitet.',
  'note.fixedNutsGdp': 'Feste NUTS-2024-Geografie. Das Gesamt-BIP von Verviers ist die exakte additive Summe aus BE335 und BE336; der nicht additive BIP-je-Einwohner-Wert ist bewusst nicht verfügbar.',
  'note.exactMunicipalityCodes': 'Gemeindecodes der Quelle werden exakt mit dem geprüften amtlichen Grenzbereich verknüpft; die übergeordnete Hierarchie stammt aus den Eigenschaften der Grenzobjekte.',
  'note.ghslFixedLau': 'Aus Fernerkundung abgeleitete und modellierte bebaute Fläche auf festen GISCO-2020-LAU-Grenzen.',
  'note.brusselsProjection': 'Amtliche Projektion nur für Brüssel auf dem festen aktuellen Gebiet der 19 Gemeinden; andere Gemeinden liegen außerhalb des Geltungsbereichs.',
  'note.sourceNativeDistrict': 'Bezirksbeobachtungen in der Geografie der Quelle; ein Wert höherer Ebene wird keiner feineren Geografie zugewiesen.',
  'note.coordinateInventory': 'Aus Koordinaten abgeleitetes Inventar; Null ist ein gültiger Wert und ausgeschlossene Punkte bleiben nicht zugeordnet.',
  'note.inventorySnapshot': 'Bestandsaufnahme zum Verarbeitungsdatum der Quelldaten; es handelt sich nicht um eine historische Flussmessung.',
  'note.gtfsCoordinates': 'GTFS-Haltestellenkoordinaten werden mit amtlichen Gemeindepolygonen mit geprüftem Hash verknüpft; keine Quellenkennung wird als Gemeindehierarchie interpretiert.',
  'note.regionalCurrentDomain': 'Aktueller Gemeindebereich ausschließlich für {scope}; alle anderen Gemeinden liegen ausdrücklich außerhalb des Geltungsbereichs.',
  'note.sourceNativeMunicipality': 'Gemeindebeobachtungen in der Geografie der Quelle; ein Wert höherer Ebene wird keiner feineren Geografie zugewiesen.',
  'note.clcIntersection': 'Amtliche CLC2018-Polygone wurden in EPSG:3035 mit amtlichen Statbel-Gemeindepolygonen von 2018 mit geprüftem Hash verschnitten.',
  'note.sparseCubeCoverage': 'Die Abdeckung des dünn besetzten Cubes wird nach Anwendung der ausgewählten Filter neu berechnet; Null bleibt ein gültiger Datenwert.',
  'note.regionalCoverage': 'Gemeinden außerhalb des angegebenen regionalen Geltungsbereichs sind ausdrücklich nicht verfügbar und nicht statistisch fehlend; von der Quelle als nicht verfügbar oder unterdrückt gekennzeichnete Status bleiben innerhalb des Bereichs getrennt.',
  'note.comparisonDeclaredGroup': 'Der direkte Vergleich ist auf diese angegebene Vergleichsgruppe und dieses unveränderliche Grenzenpaket beschränkt; es wird keine Übereinstimmung zwischen Grenzen abgeleitet.',
  'note.comparisonSourceDomain': 'Direkte Vergleiche sind auf die von der Quelle angegebene Codedomänengruppe beschränkt.',
  'note.comparisonSingleSnapshot': 'Einzelne Momentaufnahme am 1. Januar {year} auf dem ursprünglichen Grenzenpaket von {year}.',
  'note.comparisonMixedBases': 'Direkte Differenzen sind deaktiviert, weil die Vergleichsgruppe Quellenklassifikation und ursprüngliche Grenzgrundlagen mischt.',
  'note.comparisonBoundaryPacks': 'Direkte Differenzen sind deaktiviert, weil die Vergleichsgruppe mehrere ursprüngliche Grenzenpakete umfasst.',
  'note.comparisonAirFixed': 'Alle Jahre verwenden die feste Gemeindeklassifikation von IRCEL-CELINE nach {year}.',
  'note.comparisonRegional': 'Vergleich und Rangfolge sind auf die {count} aktuellen Gemeinden in {scope} beschränkt; andere Gemeinden liegen außerhalb des Geltungsbereichs und sind keine fehlenden Beobachtungen. Eine landesweite Rangfolge ist nicht gültig.',
  'note.comparisonEurostatGdp': 'Nominale Reihe zu jeweiligen Preisen auf einer festen NUTS-2024-Geografie. Eurostat kennzeichnet 2009 als Bruch und 2024 als vorläufig. Die Rangfolge je Einwohner umfasst 42 Bezirke; Verviers hat nur einen additiven Gesamt-BIP-Wert, weil seine beiden NUTS-3-Verhältnisse nicht zusammengeführt werden.',
  'table.eyebrow': 'Barrierefreie Datenansicht · {period}',
  'table.close': 'Datentabelle schließen',
  'table.filterLabel': 'Gebiete filtern',
  'table.filterPlaceholder': 'Nach Gebiet oder Code filtern',
  'table.areaCount': '{count} Gebiete',
  'table.caption': '{title}, {period}.',
  'table.sortHint': 'Wählen Sie eine Spaltenüberschrift, um diese Tabelle zu sortieren.',
  'table.sortedAscending': 'Nach {column} aufsteigend sortiert.',
  'table.sortedDescending': 'Nach {column} absteigend sortiert.',
  'table.area': 'Gebiet',
  'table.officialCode': 'Offizieller Code',
  'table.previous': '← Zurück',
  'table.next': 'Weiter →',
  'table.page': 'Seite {page} von {count}',
}

export const MESSAGES: Record<Locale, Record<MessageKey, string>> = {
  en: EN_MESSAGES,
  fr: FR_MESSAGES,
  nl: NL_MESSAGES,
  de: DE_MESSAGES,
}

export const INTL_LOCALES: Record<Locale, string> = {
  en: 'en-BE',
  fr: 'fr-BE',
  nl: 'nl-BE',
  de: 'de-BE',
}

export const LOCALE_STORAGE_KEY = 'belgium-in-maps.locale'

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALES.includes(value.toLocaleLowerCase() as Locale)
}

export function localeFromLanguageTag(value: unknown): Locale | undefined {
  if (typeof value !== 'string') return undefined
  const primary = value.trim().toLocaleLowerCase().split(/[-_]/)[0]
  return isLocale(primary) ? primary : undefined
}

export interface LocaleResolutionInput {
  search?: string
  stored?: string | null
  browserLanguages?: readonly string[]
}

export function resolveLocale({
  search = '',
  stored = null,
  browserLanguages = [],
}: LocaleResolutionInput = {}): Locale {
  const urlLocale = localeFromLanguageTag(new URLSearchParams(search).get('lang'))
  if (urlLocale) return urlLocale
  const storedLocale = localeFromLanguageTag(stored)
  if (storedLocale) return storedLocale
  for (const language of browserLanguages) {
    const browserLocale = localeFromLanguageTag(language)
    if (browserLocale) return browserLocale
  }
  return 'en'
}

export function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function saveLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Local storage can be unavailable in privacy modes; the URL remains authoritative.
  }
}

export function resolveBrowserLocale(search = window.location.search): Locale {
  return resolveLocale({
    search,
    stored: readStoredLocale(),
    browserLanguages: navigator.languages?.length ? navigator.languages : [navigator.language],
  })
}

export function translate(locale: Locale, key: MessageKey, params: MessageParams = {}): string {
  return MESSAGES[locale][key].replace(/\{(\w+)\}/g, (placeholder, name: string) => (
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : placeholder
  ))
}

export function createTranslator(locale: Locale): Translator {
  return (key, params) => translate(locale, key, params)
}

const COVERAGE_SCOPE_KEYS = {
  BELGIUM: 'details.scopeBelgium',
  FLANDERS: 'details.scopeFlanders',
  WALLONIA: 'details.scopeWallonia',
  BRUSSELS: 'details.scopeBrussels',
  PARTICIPATING_MUNICIPALITIES: 'details.scopeParticipating',
  SOURCE_DEFINED: 'details.scopeSourceDefined',
} as const satisfies Record<string, MessageKey>

/** Localize governed scope codes while leaving source-defined prose intact. */
export function coverageScopeLabel(locale: Locale, scope: string): string {
  const key = COVERAGE_SCOPE_KEYS[scope as keyof typeof COVERAGE_SCOPE_KEYS]
  return key ? translate(locale, key) : scope
}

function regionalScopeFromEnglish(locale: Locale, region: string): string {
  const code = {
    Brussels: 'BRUSSELS',
    Flanders: 'FLANDERS',
    Wallonia: 'WALLONIA',
  }[region]
  return code ? coverageScopeLabel(locale, code) : region
}

/**
 * Localizes recurring, governed catalogue disclosures that are generated from
 * stable data contracts rather than carried as free editorial prose.
 */
export function localizeCatalogNote(locale: Locale, note: string): string {
  if (locale === 'en') return note
  const normalized = note.trim()
  let match = /^AIM\/IMA (refnis\d{4}) codes were verified and normalized to bare five-digit REFNIS codes; labels are used only for Table reconciliation\.$/.exec(normalized)
  if (match) return translate(locale, 'note.aimCodes', { domain: match[1] })

  match = /^(Flanders|Wallonia)-only current municipality domain; all other municipalities are explicitly out of scope\.$/.exec(normalized)
  if (match) return translate(locale, 'note.regionalCurrentDomain', {
    scope: regionalScopeFromEnglish(locale, match[1]),
  })

  match = /^Single 1 January (\d{4}) snapshot on the native \d{4} boundary pack\.$/.exec(normalized)
  if (match) return translate(locale, 'note.comparisonSingleSnapshot', { year: match[1] })

  match = /^Comparison and ranking are limited to the (\d+) current municipalities in (Brussels|Flanders|Wallonia); municipalities elsewhere are out of scope, not missing observations\. No Belgium-wide rank is valid\.$/.exec(normalized)
  if (match) return translate(locale, 'note.comparisonRegional', {
    count: match[1],
    scope: regionalScopeFromEnglish(locale, match[2]),
  })

  match = /^All years use IRCEL-CELINE's fixed post-(\d{4}) municipality classification\.$/.exec(normalized)
  if (match) return translate(locale, 'note.comparisonAirFixed', { year: match[1] })

  const exact: Record<string, MessageKey> = {
    'Exact BIPT NIS codes are joined to the matching annual Statbel boundary pack; cross-vintage comparison is not inferred.': 'note.biptExactCodes',
    'Fixed NUTS 2024 geography. Verviers total GDP is the exact additive sum of BE335 and BE336; its non-additive GDP-per-inhabitant value is deliberately unavailable.': 'note.fixedNutsGdp',
    'Source municipality codes joined exactly to the reviewed official boundary domain; parent hierarchy comes from boundary feature properties.': 'note.exactMunicipalityCodes',
    'Remote-sensing-derived/modelled built-up surface on fixed GISCO 2020 LAU boundaries.': 'note.ghslFixedLau',
    'Brussels-only official projection on the fixed current 19-municipality domain; other municipalities are out of scope.': 'note.brusselsProjection',
    'Source-native district observations; no higher-grain value is assigned to a lower geography.': 'note.sourceNativeDistrict',
    'Coordinate-derived inventory; zero is valid data and excluded points remain unassigned.': 'note.coordinateInventory',
    'Inventory snapshot at the source data-processing date; it is not a historical flow measure.': 'note.inventorySnapshot',
    'GTFS stop coordinates joined to the hash-verified official municipality polygons; no source identifier was interpreted as a municipality hierarchy.': 'note.gtfsCoordinates',
    'Source-native municipality observations; no higher-grain value is assigned to a lower geography.': 'note.sourceNativeMunicipality',
    'Official CLC2018 polygons intersected with hash-verified official Statbel 2018 municipality polygons in EPSG:3035.': 'note.clcIntersection',
    'Sparse cube coverage is recalculated after the selected filters are applied; zero remains valid data.': 'note.sparseCubeCoverage',
    'Municipalities outside the declared regional scope are explicitly unavailable, not statistically missing; publisher-side unavailable/suppressed statuses remain separate in-scope records.': 'note.regionalCoverage',
    'Direct comparison is limited to this declared comparison group and immutable boundary pack; no cross-boundary inference is made.': 'note.comparisonDeclaredGroup',
    'Direct comparisons are limited to the source-declared code-domain group.': 'note.comparisonSourceDomain',
    'Direct deltas are disabled because the comparison set mixes source-classification and native boundary bases.': 'note.comparisonMixedBases',
    'Direct deltas are disabled because the comparison set crosses native boundary packs.': 'note.comparisonBoundaryPacks',
    'Nominal current-price series on a fixed NUTS 2024 geography. Eurostat flags 2009 as a break and 2024 as provisional. Per-inhabitant ranking covers 42 districts; Verviers has only an additive total-GDP value because its two NUTS-3 ratios are not rolled up.': 'note.comparisonEurostatGdp',
  }
  const key = exact[normalized]
  return key ? translate(locale, key) : note
}

export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(INTL_LOCALES[locale], options).format(value)
}

export function geographyLevelLabel(
  locale: Locale,
  level: 'region' | 'province' | 'district' | 'municipality' | 'sector',
  plural = false,
): string {
  const key = `level.${level}${plural ? 'Plural' : ''}` as MessageKey
  return translate(locale, key)
}

export function updateDocumentLanguage(locale: Locale): void {
  document.documentElement.lang = locale
  document.title = translate(locale, 'meta.title')
  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (description) description.content = translate(locale, 'meta.description')
}
